// ================================================================================================
import { ReactNode, Component, CSSProperties, ReactElement, lazy, Suspense } from 'react';
import * as ReactDOM from 'react-dom';
import _ from "lodash";
import { SketchPicker } from 'react-color';
import moment from "moment";
import { saveAs } from "file-saver";
import DatePicker, { RangeType as DateRangeType } from 'rsuite/DatePicker';
import DateRangePicker, { DateRange, RangeType as DateRangeRangeType } from 'rsuite/DateRangePicker';
import {
    List,
    FlexboxGrid,
    Checkbox,
    ButtonGroup,
    IconButton as RsIconButton,
    InputGroup,
    Input,
    Button as RsButton,
    Radio,
    RadioGroup,
    Slider,
    Nav
} from 'rsuite';
import ResponsiveNav from "@rsuite/responsive-nav";
import '../../Theme.less';

const emailValidationPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const ipValidationPattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

import { MultiHighlightsEditor, validateHighlightsWithMsg } from '../Widgets/Things/Gauges/GaugesHelper';
import { ColorPaletteSelector } from './ColorPaletteSelector';
import TextBox from './TextBox';
import Toggle from '@Primitive/Toggle';
import ListItem from '@Primitive/ListItem';
import "./PrimitiveCss.less";
import "./SettingsList.less";
import { getIconClassName, IconInfo } from "./IconSelectorUtils";
import { GetReactStyle, getZDepthShadow, red500, orange500, red200, blue500, grey50 } from "@Utils/Colors";
import {
    SettingsElementButton,
    SettingsElementImage,
    SettingsElementDatetimeRange,
    SettingsElementInfotip,
    SettingsType,
    SettingsElementDropDown,
    SettingsElementBoolean,
    SettingsElementDatetime,
    SettingsElementTime,
    SettingsElementNumber,
    SettingsElementSlider,
    SettingsElementString,
    SettingsElementBarcodeScanner,
    SettingsElementColorPicker,
    SettingsElementDialog,
    SettingsDialogTitleGetter,
    SettingsElementIcon,
    SettingsElementItemsMgmtDialog,
    SettingsElementListItem,
    SettingsElementMarkdownString,
    SettingsElementTextEditorString,
    SettingsElementHighlights,
    ItemsMgmtDialogInfo,
    GaugeHelperHighlight,
    GaugeHelperHighlightDictionary,
    SettingsGroup,
    SettingsElementAll,
    SettingsElementImageFileInput,
    SettingsElementGroup,
    SettingsElementExtendedIcon,
    eIconType,
    ExtendedIconUploadConf,
    DropDownItem,
    SettingsElementGeneric,
    SettingsElementListOfItems,
    ListItem as CustomListItem,
    SettingsElementCode,
    SettingsElementAttachments,
    IAttachment,
    eAttachmentAction,
    IAttachmentAction,
    SettingsElementRadioButtons,
    SettingsElementButtonGroup,
    SettingsElementTabsGroup,
} from './SettingsListType';

import { Button } from './Button';
import { InfoTipItem, InfoTipItemComponent, InfoTipLayout } from './InfoTips';
import { isDefined, isFunction, IsNullOrEmpty, IsNullOrWhiteSpace, isString, isStringEmpty, JSONSafeStringify, RunSafe, getRandomKey } from '@Utils/Micro';
import { DialogConf, ExDialog } from './ExDialog';
import { Infotip, InfotipProps } from './Infotip';
import { ListLite, ListLiteItem } from './List/ListLite';
import { DebugEx } from '@Utils/DebugEx';
import { LazyLoadingAnimation } from './Lazy';
import { ToolbarIconButton } from './ToolbarIconButton';
import { DomUtils } from '@Utils/Dom';
import { MarkdownEditor } from './Markdown/MarkdownEditor';
import { CopyToClipboard, arrayMoveImmutable } from '@Utils/Utils';
import { RenderToolbar } from '@UI/Layout/AdvanceLayout';
import { MonacoEditor } from "./Monaco";
import { BoardTheming } from "@UI/Dashboard/BoardTheming";
import { Select, SelectOption } from '@Primitive/Select';
import { PresetColor } from 'react-color/lib/components/sketch/Sketch';
import { IconButton } from '@Primitive/Button';
import Popover from '@Primitive/Popover';
import { getGlobal } from '@Utils/Globals';
import { Props } from '@Utils/ReactProps';

const IconSelector = lazy(() => import("./IconSelector"));
const Sortable = lazy(() => import("./Sortable"));
const TextEditorSettingsEditor = lazy(() => import("./TextEditor/TextEditorSettingsEditor"));
const BarcodeScanner = lazy(() => import("../Widgets/Things/Image/BarcodeScannerWidget"));

function migrateRsuiteDateFormatFromV4ToV5(format: string) {
    return format?.replace(/Y/g, 'y').replace(/D/g, 'd');
}
function convertDateFormatToMomentToken(format: string) {
    return format?.replace(/y/g, 'Y').replace(/d/g, 'D');
}
// ================================================================================================
export interface SettingsToolbarSettings {
    theme?: {
        height: number;
        bgColor: string;
        iconColor: string;
        boxShadow?: string;
    };
    showCopy?: boolean;
    showCloseIcon?: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface SettingsListProps<T> extends Props<any> {
    SettingsMap: SettingsGroup<T>[];
    Data: T;
    AutoSave?: boolean;
    UnsavedData?: boolean;
    HideUnsavedDataHighlight?: boolean;
    Toolbar?: SettingsToolbarSettings;
    onHoverHighlight?: boolean;
    onCheckData?: (data: T) => { valid: boolean, errorMsg?: string | ReactNode };
    onSaveData: (data: T) => void;
    onDataChange?: (data: T) => void;
    onCancelClick?: () => void;
    onElementChanged?: (dataPath: string, data: any, prevData: any, formData: any) => void;
    onButtonPressed?: (buttonElement: any, formData: T) => void;
    style?: CSSProperties;

    /**
     * Define the number of grid column
     * Default: 4
     */
    numberOfGridColumn?: number;

    /**
     * Space between grid cells
     * Default: 5
     */
    gridGap?: number;
}
// ------------------------------------------------------------------------------------------------
export interface SettingsListState {
    Data: any;
    UnsavedData: boolean;
    tempScannerValue: { [key: string]: string };
    dialogConf: DialogConf;
    dialogPrivData: any;
    uploadingFile: boolean;
    uploadFileErrorMessage: { [key: string]: string };
    tooltip: InfotipProps;
    subGroupOpen: { [key: string]: boolean };
    validationErrors: { [key: string]: string };
    editedAttachmentName: string;
    uploadingAttachment: boolean;
    tabsActive: { [key: string]: string };
}
// ------------------------------------------------------------------------------------------------
// Global variable that can be used for caching the date ranges for faster rendering
var cachedDateRanges: { ranges: any, tsDay: number } = null;
// ------------------------------------------------------------------------------------------------
export class SettingsList<T> extends Component<SettingsListProps<T>, SettingsListState> {
    // --------------------------------------------------------------------------------------------
    // links from data path to search value
    itemsListSearch: { [key: string]: string } = {};
    // --------------------------------------------------------------------------------------------
    constructor(props: SettingsListProps<T>) {
        super(props);
        this.state = {
            Data: _.cloneDeep(props.Data),
            UnsavedData: this.props.UnsavedData,
            tempScannerValue: {},
            dialogConf: null,
            dialogPrivData: null,
            uploadingFile: false,
            uploadFileErrorMessage: {},
            tooltip: {
                open: false,
                content: '',
                xPos: 0,
                yPos: 0
            },
            subGroupOpen: {},
            validationErrors: {},
            tabsActive: {},
            editedAttachmentName: null,
            uploadingAttachment: false
        };
    }
    // --------------------------------------------------------------------------------------------
    static getDerivedStateFromProps(nextProps: SettingsListProps<any>, prevState: SettingsListState) {
        if (isDefined(nextProps.UnsavedData) &&
            prevState.UnsavedData != nextProps.UnsavedData)
            return { UnsavedData: nextProps.UnsavedData };
        return null;
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: SettingsListProps<any>, prevState: SettingsListState) {
        if (!_.isEqual(this.props.Data, prevProps.Data))
            this.setState({
                Data: _.cloneDeep(this.props.Data),
                UnsavedData: this.props.UnsavedData
            }, this.validateElements);

        if (!_.isEqual(this.props.SettingsMap, prevProps.SettingsMap))
            this.validateElements();
    }
    // --------------------------------------------------------------------------------------------
    saveData = () => {
        const data = this.state.Data;
        if (isDefined(this.props.onCheckData)) {
            let v = this.props.onCheckData(data);
            if (!v.valid) {
                this.setState({
                    dialogConf: {
                        title: "Error",
                        actions: [<IconButton
                            key="close-btn"
                            tooltip="Close"
                            iconClassName="fas fa-times"
                            iconStyle={{ color: red200 }}
                            onClick={() => this.setState({ dialogConf: null })}
                        />],
                        body: <div style={{ padding: 24 }}>{v.errorMsg}</div>
                    }
                });
                return;
            }
        }

        if (this.state.UnsavedData) {
            this.setState({ UnsavedData: false });

            // Inform parent for the save?
            const cb = this.props.onSaveData;
            if (_.isFunction(cb)) cb(_.cloneDeep(data));
        }
    }
    // --------------------------------------------------------------------------------------------
    cancelData = () => {
        this.setState({
            Data: _.cloneDeep(this.props.Data),
            UnsavedData: false
        });
        // Inform parent for the cancel?
        const cb = this.props.onCancelClick;
        if (_.isFunction(cb)) cb();
    }
    // --------------------------------------------------------------------------------------------
    getValue(data: any, path: string): any {
        if (IsNullOrEmpty(path))
            return data;
        return _.get(data, path);
    }
    // --------------------------------------------------------------------------------------------
    setValueState(state: SettingsListState, path: string, value: any) {
        if (IsNullOrEmpty(path)) {
            state.Data = value;
            this.onDataChange(state.Data);
        }
        else
            this.setValue(state.Data, path, value);
    }
    // --------------------------------------------------------------------------------------------
    setValue(data: any, path: string, value: any) {
        _.set(data, path, value);
        this.onDataChange(data);
    }
    // --------------------------------------------------------------------------------------------
    onDataChange = data => {
        // Inform parent for the change?
        const cb = this.props.onDataChange;
        if (_.isFunction(cb)) cb(data);
    }
    // --------------------------------------------------------------------------------------------
    autoSave = () => {
        this.validateElements();
        if (this.props.AutoSave)
            this.saveData();
    }
    // --------------------------------------------------------------------------------------------
    dropDownMenuGroupLabeling(data) {
        return <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        }}>
            <span>{data.label}</span>
            <span style={{
                backgroundColor: '#EBECF0',
                borderRadius: '2em',
                color: '#172B4D',
                display: 'inline-block',
                fontSize: 12,
                fontWeight: 'normal',
                lineHeight: '1',
                minWidth: 1,
                padding: '0.16666666666667em 0.5em',
                textAlign: 'center',
            }}>{data.options.length}</span>
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    renderDropDownMenu(key: string, element: SettingsElementDropDown<any>, groupLevel: number) {
        let errorStr = this.state.validationErrors[element.dataPath];
        let value = this.getValue(this.state.Data, element.dataPath);
        const disabled = element.readonly || element.disable;
        const { onElementChanged } = this.props;
        const rightItemStyle = (element.inSeperateLine) ? { width: "100%" } : this.getRightItemStyleWidth(element);
        const isMulti = element.isMulti === true;
        const isCreatable = element.isCreatable === true;
        const isGrouped = element.isGroupedItems === true;
        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={(element.inSeperateLine) ? {} : this.getTextItemStyleWidth(element)}
            rightItemStyle={{
                cursor: disabled ? 'not-allowed' : 'initial',
                ...rightItemStyle
            }}
            rightItemInSeperateLine={element?.inSeperateLine}
            rightItem={
                <div style={{ width: "100%" }}>
                    <Select
                        options={_.map(element.items, item => {
                            let option: SelectOption = {
                                label: item.label,
                                value: item.value
                            };
                            if (_.isArray(item.options)) {
                                option.options = _.map(item.options, o => ({
                                    label: o.label,
                                    value: o.value
                                }));
                            }
                            return option;
                        })}
                        isMulti={isMulti}
                        maxNumOfSelections={element.maxNumOfSelections}
                        isClearable={element.isClearable === true}
                        closeMenuOnSelect={element.closeMenuOnSelect}
                        isCreatable={isCreatable}
                        valueStr={value}
                        isDisabled={disabled}
                        dialogTitle={element.label}
                        useDialog={element.useDialog}
                        hideSelectAll={element.hideSelectAll}
                        hideSelectedOptions={element.hideSelectedOptions}
                        isSearchableWhenUsingDialog={element.isSearchableWhenUsingDialog}
                        formatGroupLabel={element.isGroupedItems ? this.dropDownMenuGroupLabeling : undefined}
                        onChange={(data: any) => {
                            this.setState((s: SettingsListState) => {
                                // keep previous data
                                const prevData = this.getValue(s.Data, element.dataPath);

                                // set new data
                                this.setValueState(s, element.dataPath, handleSelectChange(data, element.items, isMulti, isCreatable, isGrouped));

                                if (isFunction(onElementChanged)) {
                                    const newData = this.getValue(s.Data, element.dataPath);
                                    if (newData != prevData)
                                        onElementChanged(
                                            element.dataPath,
                                            newData,
                                            prevData,
                                            s.Data);
                                }

                                s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                return s;
                            }, this.autoSave);
                        }}
                        containerStyle={{ width: "100%" }}
                    />
                    {(errorStr != null) ? <div className="settingslist-error" >{errorStr}</div> : null}
                </div>
            }
        />;
    }
    // --------------------------------------------------------------------------------------------
    renderBoolean(key: string, element: SettingsElementBoolean<any>, groupLevel: number) {
        const prevData = this.getValue(this.state.Data, element.dataPath);
        const inSeperateLine = isDefined(element.oneLine) ? !element.oneLine : false;

        const { onElementChanged } = this.props;
        if (element.layout == 'checkbox')
            return <ListLiteItem
                primaryText={element.label}
                tooltip={element.tooltip}
                key={key}
                groupLevel={groupLevel}
                textStyle={{ justifyContent: "unset" }}
                leftItem={
                    <Checkbox
                        checked={prevData}
                        disabled={element.readonly || element.disable}
                        onChange={(value, isInputChecked: boolean) => {
                            this.setState((s: SettingsListState) => {
                                this.setValueState(s, element.dataPath, isInputChecked);
                                if (isFunction(onElementChanged)) {
                                    const newData = this.getValue(s.Data, element.dataPath);
                                    if (newData != prevData)
                                        onElementChanged(
                                            element.dataPath,
                                            newData,
                                            prevData,
                                            s.Data);
                                }
                                s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                return s;
                            }, this.autoSave);
                        }}
                    />
                }
            />;

        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={<Toggle
                key={key + "-toggle"}
                disabled={element.readonly || element.disable}
                onToggle={(isInputChecked: boolean) => {
                    this.setState((s: SettingsListState) => {
                        this.setValueState(s, element.dataPath, isInputChecked);
                        if (isFunction(onElementChanged)) {
                            const newData = this.getValue(s.Data, element.dataPath);
                            if (newData != prevData)
                                onElementChanged(
                                    element.dataPath,
                                    newData,
                                    prevData,
                                    s.Data);
                        }
                        s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                        return s;
                    }, this.autoSave);
                }}
                toggled={prevData}
            />}
        />;
    }
    // --------------------------------------------------------------------------------------------
    renderDatetime(key: string, element: SettingsElementDatetime<any>, groupLevel: number) {
        const value = this.getValue(this.state.Data, element.dataPath);
        const date = !IsNullOrEmpty(value) ? moment(value).toDate() : null;
        const defaultValue = moment.isDate(element.defaultValue) ? element.defaultValue : null;
        const inSeperateLine = isDefined(element.oneLine) ? !element.oneLine : true;
        const placement = isDefined(element.placement) ? element.placement : "auto";
        const isDisabled = element.readonly || element.disable;
        const errorStr = this.state.validationErrors[element.dataPath];
        const format = migrateRsuiteDateFormatFromV4ToV5(element.format) ?? 'yyyy-MM-dd HH:mm:ss';

        // are any custom ranges provided?
        let ranges: DateRangeType<Date>[] = [];
        if (isDefined(element.ranges)) {
            ranges = element.ranges;
        } else {
            // add the default ranges (today and yesterday)
            ranges.push({
                label: 'Today',
                value: new Date()
            });

            const yesterday = new Date();
            yesterday.setDate(new Date().getDate() - 1);
            ranges.push({
                label: 'Yesterday',
                value: yesterday
            });
        }

        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={!inSeperateLine ? this.getTextItemStyleWidth(element) : {}}
            rightItemStyle={!inSeperateLine ? this.getRightItemStyleWidth(element) : { width: '100%' }}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={<div style={{ width: '100%' }}>
                <DatePicker
                    editable={false}
                    disabled={isDisabled}
                    style={{
                        width: '100%',
                        color: element.style?.fontColor
                    }}
                    block
                    isoWeek={element.isoWeek}
                    ranges={ranges}
                    placement={placement}
                    format={format}
                    defaultValue={defaultValue}
                    value={date}
                    onChange={data => {
                        try {
                            let value = !isDefined(data) ? null : data.toISOString();

                            this.setState((state: SettingsListState) => {
                                // get previous value before update
                                let prevData = this.getValue(state.Data, element.dataPath);
                                this.setValueState(state, element.dataPath, value);

                                // emit change event
                                const callback = this.props.onElementChanged;
                                if (isFunction(callback))
                                    callback(element.dataPath, value, prevData, state.Data);

                                state.UnsavedData = !_.isEqual(state.Data, this.props.Data);
                                return state;
                            }, this.autoSave);
                        } catch (ex) {
                            DebugEx.TraceException(ex);
                        }
                    }}
                    disabledDate={date => {
                        if (element.disabledWeekends) {
                            return date.toString().indexOf('Sat') > -1
                                || date.toString().indexOf('Sun') > -1;
                        } else {
                            return false;
                        }
                    }} />
                {errorStr != null ? <div className='settingslist-error'>{errorStr}</div> : null}
            </div>}
        />;
    }
    // --------------------------------------------------------------------------------------------
    renderDatetimeRange(key: string, element: SettingsElementDatetimeRange<any>, groupLevel: number) {
        let value = this.getValue(this.state.Data, element.dataPath);
        let valueArray: DateRange = [null, null];
        let isDisabled = element?.readonly || element?.disable;
        let errorStr = this.state.validationErrors[element.dataPath];
        let errorJsx = errorStr != null ? <div className='settingslist-error'>{errorStr}</div> : null;
        let withNavigation = element.withNavigation;

        if (_.isObject(value)) {
            valueArray = [
                moment((value as any).start).toDate(),
                moment((value as any).end).toDate()
            ];
        }

        // are any custom ranges provided?
        let ranges: DateRangeRangeType[] = [];
        if (isDefined(element.ranges)) {
            ranges = element.ranges;
        } else {

            // If is cached and is from this day reused it
            if (cachedDateRanges != null &&
                cachedDateRanges.tsDay == new Date().getDate()) {
                ranges = cachedDateRanges.ranges;
            } else {
                // add the default ranges (today, yesterday and last 7 days)
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                const todayEnd = new Date();
                todayEnd.setHours(23, 59, 59, 99);

                ranges.push({
                    label: 'Today',
                    value: [todayStart, todayEnd]
                });

                const yesterdayStart = new Date();
                yesterdayStart.setDate(new Date().getDate() - 1);
                yesterdayStart.setHours(0, 0, 0, 0);

                const yesterdayEnd = new Date();
                yesterdayEnd.setDate(new Date().getDate() - 1);
                yesterdayEnd.setHours(23, 59, 59, 99);
                ranges.push({
                    label: 'Yesterday',
                    value: [yesterdayStart, yesterdayEnd]
                });

                const last7Days = new Date();
                last7Days.setDate(new Date().getDate() - 6);
                last7Days.setHours(0, 0, 0, 0);
                ranges.push({
                    label: 'Last 7 days',
                    value: [last7Days, todayEnd]
                });

                // add last 30 days to the defaults
                const last30Days = new Date();
                last30Days.setDate(new Date().getDate() - 29);
                last30Days.setHours(0, 0, 0, 0);
                ranges.push({
                    label: 'Last 30 days',
                    value: [last30Days, todayEnd]
                });

                const firstDayOfMonth = new Date();
                firstDayOfMonth.setDate(1);

                // find how many days this month has
                const lastDayOfMonth = new Date();
                switch (lastDayOfMonth.getMonth()) {
                    case 0: case 2: case 4: case 6: case 7: case 9: case 11:
                        lastDayOfMonth.setDate(31);
                        break;
                    case 3: case 5: case 8: case 10:
                        lastDayOfMonth.setDate(30);
                        break;
                    case 1:
                        lastDayOfMonth.setDate(28);
                        break;
                    default:
                        break;
                }

                // add `this month` to the defaults
                ranges.push({
                    label: 'This month',
                    value: [firstDayOfMonth, lastDayOfMonth]
                });

                const firstDayOfLastMonth = new Date();
                firstDayOfLastMonth.setMonth(firstDayOfLastMonth.getMonth() - 1);
                firstDayOfLastMonth.setDate(1);

                const lastDayOfLastMonth = new Date();
                lastDayOfLastMonth.setMonth(lastDayOfLastMonth.getMonth() - 1);

                // find how many days last month has
                switch (lastDayOfLastMonth.getMonth()) {
                    case 0: case 2: case 4: case 6: case 7: case 9: case 11:
                        lastDayOfLastMonth.setDate(31);
                        break;
                    case 3: case 5: case 8: case 10:
                        lastDayOfLastMonth.setDate(30);
                        break;
                    case 1:
                        lastDayOfLastMonth.setDate(28);
                        break;
                    default:
                        break;
                }

                // add `last month` to the defaults
                ranges.push({
                    label: 'Last month',
                    value: [firstDayOfLastMonth, lastDayOfLastMonth]
                });

                // Saved for next renders
                cachedDateRanges = {
                    ranges: ranges,
                    tsDay: todayStart.getDate()
                };
            }
        }

        let className: string;
        let inputStyle: CSSProperties = { width: '100%' };

        if (withNavigation) {
            className = 'no-border';
            inputStyle = {
                width: 'calc(100% - 55px)',
                border: 'none',
                borderRadius: 0
            };
        }

        let menuClassName = "";
        if (element.isMobileView) {
            menuClassName = "mobile";
        }

        let dateFormatMoment = convertDateFormatToMomentToken(element.format) ?? 'YYYY-MM-DD';
        let dateFormat = migrateRsuiteDateFormatFromV4ToV5(element.format) ?? 'yyyy-MM-dd';

        let placement = isDefined(element.placement) ? element.placement : 'auto';
        let dateRangePicker = (
            <DateRangePicker
                block
                editable={false}
                showOneCalendar={dateFormat?.indexOf('HH:mm') == -1 && dateFormat?.indexOf('hh:mm') == -1}
                isoWeek={element.isoWeek}
                disabled={isDisabled}
                ranges={ranges}
                placement={placement}
                format={dateFormat}
                style={inputStyle}
                className={className}
                menuClassName={menuClassName}
                value={valueArray}
                onChange={data => {
                    try {
                        let value = (_.isEmpty(data)) ? null : {
                            start: data[0].toISOString(),
                            end: data[1].toISOString()
                        };

                        this.setState((state: SettingsListState) => {
                            let prevData = { ...this.getValue(state.Data, element.dataPath) };
                            this.setValueState(state, element.dataPath, value);

                            const callback = this.props.onElementChanged;
                            if (isFunction(callback)) {
                                callback(element.dataPath, value, prevData, state.Data);
                            }

                            state.UnsavedData = !_.isEqual(state.Data, this.props.Data);
                            return state;
                        }, this.autoSave);
                    } catch (ex) {
                        DebugEx.TraceException(ex);
                    }
                }}
                disabledDate={date => {
                    if (element.disabledWeekends) {
                        return date.toString().indexOf('Sat') > -1
                            || date.toString().indexOf('Sun') > -1;
                    } else {
                        return false;
                    }
                }} />
        );

        if (!withNavigation && errorJsx != null) {
            dateRangePicker = (
                <div style={{ width: '100%' }}>
                    {dateRangePicker}
                    {errorJsx}
                </div>
            );
        }

        const navigationArrow = (direction: string) => {
            let icon = null;
            let tooltip;

            // decorate element
            switch (direction) {
                case 'left':
                    icon = <i className='fas fa-angle-left' />;
                    if (IsNullOrWhiteSpace(element.leftArrowTooltip)) {
                        tooltip = 'Back';
                    } else {
                        tooltip = element.leftArrowTooltip;
                    }
                    break;
                case 'right':
                    icon = <i className='fas fa-angle-right' />;
                    if (IsNullOrWhiteSpace(element.rightArrowTooltip)) {
                        tooltip = 'Forth';
                    } else {
                        tooltip = element.rightArrowTooltip;
                    }
                    break;
                default:
                    return null;
            }

            // icon button for navigation
            return (
                <RsIconButton
                    icon={icon}
                    style={{
                        width: 34,
                        height: 34,
                        backgroundColor: 'white',
                        // same as inner spans
                        color: '#8e8e93'
                    }}
                    disabled={element.readonly || element.disable}
                    onMouseEnter={event => {
                        this._showTooltip(tooltip, event.pageX - 20, event.pageY);
                    }}
                    onMouseLeave={() => {
                        this._hideTooltip();
                    }}
                    onClick={() => {
                        let dateRange: { start: string, end: string } = {
                            ...this.getValue(this.state.Data, element.dataPath)
                        };
                        let prevData = { ...dateRange };

                        let startDate = moment.utc(dateRange.start);
                        let endDate = moment.utc(dateRange.end);

                        // get the difference in days
                        let daysDiff = startDate.diff(endDate, 'days');
                        if (daysDiff == 0) {
                            daysDiff = 1;
                        }
                        daysDiff = Math.abs(daysDiff);

                        // shift period
                        if (direction == 'left') {
                            startDate = startDate.subtract(daysDiff, 'days');
                            endDate = endDate.subtract(daysDiff, 'days');
                        } else if (direction == 'right') {
                            startDate = startDate.add(daysDiff, 'days');
                            endDate = endDate.add(daysDiff, 'days');
                        }

                        dateRange.start = startDate.format(dateFormatMoment);
                        dateRange.end = endDate.format(dateFormatMoment);

                        this.setState((state: SettingsListState) => {
                            // update the date range
                            this.setValueState(state, element.dataPath, dateRange);
                            state.UnsavedData = !_.isEqual(state.Data, this.props.Data);

                            const callback = this.props.onElementChanged;
                            if (isFunction(callback)) {
                                callback(element.dataPath, dateRange, prevData, state.Data);
                            }

                            return state;
                        }, this.autoSave);
                    }}
                />
            );
        };

        let dateRangePickerWithNavigation = (
            <ButtonGroup
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                {navigationArrow('left')}
                {dateRangePicker}
                {navigationArrow('right')}
            </ButtonGroup>
        );

        if (withNavigation && errorJsx != null) {
            dateRangePickerWithNavigation = (
                <div style={{ width: '100%' }}>
                    {dateRangePickerWithNavigation}
                    {errorJsx}
                </div>
            );
        }

        let inSeperateLine = isDefined(element.oneLine) ? !element.oneLine : true;
        return <ListLiteItem
            key={key}
            primaryText={element.label}
            tooltip={element.tooltip}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={!inSeperateLine ? this.getTextItemStyleWidth(element) : {}}
            rightItemStyle={!inSeperateLine ? this.getRightItemStyleWidth(element) : { width: '100%' }}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={withNavigation ? dateRangePickerWithNavigation : dateRangePicker} />;
    }
    // --------------------------------------------------------------------------------------------
    renderTime(key: string, element: SettingsElementTime<any>, groupLevel: number) {
        let value = this.getValue(this.state.Data, element.dataPath);
        let date = isDefined(value) ? moment(value, 'HH:mm:ss').toDate() : null;
        let inSeperateLine = isDefined(element.oneLine) ? !element.oneLine : true;
        let isDisabled = element.readonly || element.disable;
        let errorStr = this.state.validationErrors[element.dataPath];

        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={!inSeperateLine ? this.getTextItemStyleWidth(element) : {}}
            rightItemStyle={!inSeperateLine ? this.getRightItemStyleWidth(element) : { width: '100%' }}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={<div style={{ width: '100%' }}>
                <DatePicker
                    block
                    editable={false}
                    key='time-el'
                    format='HH:mm'
                    placement='auto'
                    disabled={isDisabled}
                    style={{ color: element.style?.fontColor ?? 'inherit' }}
                    value={isValidDate(date) ? date : null}
                    onChange={time => {
                        try {
                            let value = moment(time).format('HH:mm:ss');
                            this.setState((state: SettingsListState) => {
                                // get previous value before update
                                let prevData = this.getValue(state.Data, element.dataPath);
                                this.setValueState(state, element.dataPath, value);

                                // emit change event
                                const callback = this.props.onElementChanged;
                                if (isFunction(callback))
                                    callback(element.dataPath, value, prevData, state.Data);

                                state.UnsavedData = !_.isEqual(state.Data, this.props.Data);
                                return state;
                            }, this.autoSave);
                        } catch (ex) {
                            DebugEx.TraceException(ex);
                        }
                    }}
                />
                {errorStr != null ? <div className='settingslist-error'>{errorStr}</div> : null}
            </div>}
        />
    }
    // --------------------------------------------------------------------------------------------
    renderNumber(key: string, element: SettingsElementNumber<any>, groupLevel: number) {
        let errorStr = this.state.validationErrors[element.dataPath];
        let value = this.getValue(this.state.Data, element.dataPath);
        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={element.oneLine ? this.getTextItemStyleWidth(element) : {}}
            rightItemStyle={element.oneLine ? this.getRightItemStyleWidth(element) : { width: "100%" }}
            rightItemInSeperateLine={!element.oneLine}
            rightItem={
                <TextBox
                    key={key + "-text"}
                    type="number"
                    step={element.step}
                    value={value}
                    disabled={element.disable}
                    readonly={element.readonly}
                    min={element.minValue}
                    max={element.maxValue}
                    prefix={element.prefix}
                    prefixIcon={element.prefixIcon}
                    prefixIsButton={element.prefixIsButton}
                    postfix={element.postfix}
                    postfixIcon={element.postfixIcon}
                    postfixIsButton={element.postfixIsButton}
                    errorText={errorStr}
                    // todo $1: validate min/max onblur!
                    onChangeString={(newValue: string) => {
                        try {
                            // Parse value
                            let _value: number = null;
                            if (_.isNumber(newValue))
                                _value = newValue;
                            else if (!IsNullOrWhiteSpace(newValue))
                                _value = element.isDecimal ? parseFloat(newValue) : parseInt(newValue, 10);

                            // Check limits
                            if (isDefined(element.minValue) && _value < element.minValue)
                                _value = element.minValue;
                            if (isDefined(element.maxValue) && _value > element.maxValue)
                                _value = element.maxValue;

                            // Update the value
                            this.setState((s: SettingsListState) => {
                                this.setValueState(s, element.dataPath, _value);
                                s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                return s;
                            }, this.autoSave);
                        } catch (ex) {
                            DebugEx.TraceException(ex);
                        }
                    }}
                    onClick={(isPrefix) => {
                        // TODO: see if we need to have special care if is prefix
                        if (isFunction(this.props.onButtonPressed))
                            this.props.onButtonPressed(element, this.state.Data);
                    }}
                    autoFocus={element.autoFocus}
                />
            }
        />;
    }
    // --------------------------------------------------------------------------------------------
    renderSlider(key: string, element: SettingsElementSlider<any>, groupLevel: number) {
        let value = this.getValue(this.state.Data, element.dataPath);

        const hasLabel = !IsNullOrWhiteSpace(element.label);
        const hasPostfix = !IsNullOrWhiteSpace(element.valuePosfix);

        // set header (label, value, postfix)
        let label = '';
        if (element.hideValue) {
            if (hasLabel) {
                label = element.label;
            }
        } else {
            if (hasLabel) {
                label += `${element.label} `;
            }
            if (isDefined(value)) {
                if (hasPostfix) {
                    label += `[${value}${element.valuePosfix}]`;
                } else {
                    label += `[${value}]`;
                }
            } else {
                if (hasPostfix) {
                    label += `[${element.valuePosfix}]`;
                }
            }
        }

        const slider = (
            <>
                {!IsNullOrWhiteSpace(element.icon)
                    ? <i className={element.icon} style={{ paddingRight: 16 }} />
                    : null}
                <Slider
                    progress
                    handleClassName='slider-handle'
                    tooltip={element.hideValue == true}
                    key={key + '-slider'}
                    value={value}
                    min={element.minValue}
                    max={element.maxValue}
                    step={element.stepValue}
                    disabled={element.readonly || element.disable}
                    onChange={newValue => {
                        // get previous value before update
                        let prevValue = this.getValue(this.state.Data, element.dataPath);
                        if (prevValue == newValue)
                            return;

                        this.setState((s: SettingsListState) => {
                            this.setValueState(s, element.dataPath, newValue);

                            // emit change event
                            const callback = this.props.onElementChanged;
                            if (isFunction(callback))
                                callback(element.dataPath, newValue, prevValue, this.state.Data);

                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave)
                    }} />
                {!IsNullOrWhiteSpace(element.rightIcon)
                    ? <i className={element.rightIcon} style={{ paddingLeft: 16 }} />
                    : null}
            </>
        );

        return (
            <ListLiteItem
                ref={ref => {
                    if (ref != null && ref instanceof HTMLDivElement) {
                        let node = ref as HTMLDivElement;
                        if (node != null) {
                            // find bar and apply coloring
                            let bar = node.querySelector('.rs-slider-progress-bar') as HTMLDivElement;
                            if (bar != null) {
                                if (!IsNullOrWhiteSpace(element.color)) {
                                    bar.style.backgroundColor = element.color;
                                } else {
                                    bar.style.backgroundColor = '#3498ff';
                                }
                            }
                            // find circle and apply coloring
                            let circle = node.querySelector('.rs-slider-handle') as HTMLDivElement;
                            if (circle != null) {
                                if (!IsNullOrWhiteSpace(element.color)) {
                                    circle.style.borderColor = element.color;
                                } else {
                                    circle.style.borderColor = '#3498ff';
                                }
                            }
                        }
                    }
                }}
                key={key}
                groupLevel={groupLevel}
                primaryText={label}
                tooltip={element.tooltip}
                leftItem={element.leftIcon}
                textStyle={element.oneLine ? this.getTextItemStyleWidth(element) : {}}
                rightItemInSeperateLine={!element.oneLine}
                rightItemStyle={!element.oneLine ? { width: '100%' } : this.getRightItemStyleWidth(element)}
                rightItem={!element.oneLine
                    ? <div style={{ display: 'flex', alignItems: 'center' }}>
                        {slider}
                    </div>
                    : slider} />
        );
    }
    // --------------------------------------------------------------------------------------------
    getRightItemStyleWidth(element: SettingsElementString<any>
        | SettingsElementNumber<any>
        | SettingsElementDropDown<any>
        | SettingsElementBarcodeScanner<any>
        | SettingsElementDatetime<any>
        | SettingsElementDatetimeRange<any>
        | SettingsElementTime<any>
        | SettingsElementRadioButtons<any>
        | SettingsElementSlider<any>
    ) {
        if (!isDefined(element.widthInput))
            return { width: 100 }
        if (_.isNumber(element.widthInput) && element.widthInput >= 0 && element.widthInput <= 1)
            return { width: element.widthInput * 100 + "%" }
        return { width: element.widthInput }
    }
    // --------------------------------------------------------------------------------------------
    getTextItemStyleWidth(element: SettingsElementString<any>
        | SettingsElementNumber<any>
        | SettingsElementDropDown<any>
        | SettingsElementBarcodeScanner<any>
        | SettingsElementDatetime<any>
        | SettingsElementDatetimeRange<any>
        | SettingsElementTime<any>
        | SettingsElementRadioButtons<any>
        | SettingsElementSlider<any>
    ) {
        if (_.isNumber(element.widthInput) && element.widthInput >= 0 && element.widthInput <= 1)
            return { width: (1 - element.widthInput) * 100 + "%" }
        return {}
    }
    // --------------------------------------------------------------------------------------------
    renderString(key: string, element: SettingsElementString<any>, groupLevel: number) {
        let errorStr = this.state.validationErrors[element.dataPath];
        let value = this.getValue(this.state.Data, element.dataPath);
        const inSeperateLine = !element.oneLine;

        let fieldType: any = 'text';
        let style: CSSProperties = {};

        if (element.isTextArea) {
            fieldType = 'textarea';
            style.minWidth = 'unset';
            style.resize = 'vertical';
        } else if (isDefined(element.fieldType)) {
            fieldType = element.fieldType;
        }

        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={element.oneLine ? this.getTextItemStyleWidth(element) : {}}
            rightItemStyle={inSeperateLine ? { width: "100%" } : this.getRightItemStyleWidth(element)}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={
                <TextBox
                    type={fieldType}
                    key={element.type + "-" + element.label + "-text" + "-" + groupLevel}
                    value={value}
                    disabled={element.disable}
                    readonly={element.readonly}
                    errorText={errorStr}
                    onChangeString={(value) => {
                        this.setState((s: SettingsListState) => {
                            this.setValueState(s, element.dataPath, value);
                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave);
                    }}
                    onEnterPress={() => {
                        if (IsNullOrWhiteSpace(element.onEnterPressButtonDataPath))
                            return;

                        const getElements = (x: SettingsElementGroup<T> | SettingsGroup<T>) => {
                            if (x.elements != null)
                                return x.elements;
                            if (x.elementsFunc != null)
                                return x.elementsFunc(this.state.Data);
                            return [];
                        };

                        // find button to press manually
                        let button = _.find(_.flatMapDeep(_.flatMapDeep(_.flatMapDeep(_.compact(_.flatMapDeep(this.props.SettingsMap, getElements)),
                            x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x),
                            x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x),
                            x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x),
                            x => (x.type == SettingsType.Button || x.type == SettingsType.ButtonStr) &&
                                x.dataPath == element.onEnterPressButtonDataPath) as SettingsElementButton<any>;
                        if (button == null)
                            return;

                        let isAdvanced = isDefined(button.advancedTooltip) || isDefined(button.advancedPopup);
                        let isDisabled = button.readonly || button.disable;

                        // disable if mandatory dependencies are not filled
                        let mandatoryDataPaths = null;
                        if (_.isArray(button.mandatoryDataPaths) && button.mandatoryDataPaths.length > 0) {
                            mandatoryDataPaths = button.mandatoryDataPaths;
                            isDisabled = !this.validateElements(mandatoryDataPaths, true);
                        }
                        if (!isDisabled
                            && isFunction(this.props.onButtonPressed)
                            && (button.forceSend || this.validateElements(mandatoryDataPaths))
                            && (!isAdvanced || button.splitButton)) {
                            this.props.onButtonPressed(button, this.state.Data);
                        }
                    }}
                    prefix={element.prefix}
                    prefixIcon={element.prefixIcon}
                    prefixIsButton={element.prefixIsButton}
                    postfix={element.postfix}
                    postfixIcon={element.postfixIcon}
                    postfixIsButton={element.postfixIsButton}
                    rows={(element.isTextArea) ? 4 : 1}
                    style={style}
                    onClick={(isPrefix) => {
                        // TODO: see if we need to have special care if is prefix
                        if (isFunction(this.props.onButtonPressed))
                            this.props.onButtonPressed(element, this.state.Data);
                    }}
                    autoFocus={element.autoFocus}
                    placeholder={element.placeholder}
                />
            }
        />;
    }
    // --------------------------------------------------------------------------------------------
    renderInfoTip(key: string, element: SettingsElementInfotip<any>, groupLevel: number) {
        let level = groupLevel + 1;
        return <div
            key={key}
            style={{ paddingLeft: (level > 0) ? level * 16 : undefined }}>
            {_.map(element.items, (i: InfoTipItem, idx: number) =>
                <InfoTipItemComponent
                    key={`infotip-${key}-${idx}`}
                    infoTipItem={i}
                    idx={idx}
                    infoTip={{}}
                    infoTheme={{
                        FontColor: undefined,
                        FontSize: undefined,
                        BackgroundColor: undefined,
                        BorderRadius: 0,
                        Layout: InfoTipLayout.Default
                    }}
                    handlePopupDataChange={_.noop}
                    fontColor={undefined}
                />
            )}
        </div>
    }
    // --------------------------------------------------------------------------------------------
    renderColorPicker(key: string, groupName: string, element: SettingsElementColorPicker<any>, groupLevel: number) {
        const inSeperateLine = isDefined(element.oneLine) ? !element.oneLine : false;
        const disabled = element.readonly || element.disable;

        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.isNullable
                ? <Checkbox
                    defaultChecked={isDefined(this.getValue(this.state.Data, element.dataPath))}
                    disabled={disabled}
                    onChange={(event, checked) => {
                        this.setState((s: SettingsListState) => {
                            if (checked)
                                this.setValue(s.Data, element.dataPath, "white"); // TODO: define default value !!!
                            else
                                _.unset(s.Data, element.dataPath);
                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave);
                    }}
                />
                : element.leftIcon}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={this.CreateColorPicker(
                this.state.Data,
                element.dataPath,
                !disabled,
                element.disableAlpha == true,
                element.presetColors
            )}
        />;
    }
    // --------------------------------------------------------------------------------------------
    renderImage(key: string, element: SettingsElementImage<any>, groupLevel: number) {
        let value = this.getValue(this.state.Data, element.dataPath);

        if (IsNullOrWhiteSpace(value))
            value = element.defaultImageSrc;

        if (IsNullOrWhiteSpace(value))
            return null;

        return <img
            key={key}
            src={value}
            style={{
                width: "100%", height: "100%",
                objectFit: element.imageFitPolicy,
                paddingLeft: (groupLevel > 0) ? groupLevel * 16 : undefined,
            }}
        />
    }
    // --------------------------------------------------------------------------------------------
    private CreateColorPicker(
        data: any, dataPath: string, isEnabled: boolean,
        disableAlpha: boolean, presetColors: PresetColor[]): ReactElement<any> {
        return <div>
            <Popover
                outsideNode={<div
                    style={{
                        width: 46,
                        height: 24,
                        padding: '5px',
                        background: '#fff',
                        borderRadius: '1px',
                        boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                        cursor: isEnabled ? 'pointer' : 'not-allowed',
                        opacity: isEnabled ? 1 : 0.5,
                        margin: "0 auto"
                    }}>
                    <div style={{
                        width: '36px',
                        height: '14px',
                        borderRadius: '2px',
                        background: this.getValue(data, dataPath)
                    }} />
                </div>}
                boxNode={<SketchPicker
                    disableAlpha={disableAlpha}
                    presetColors={presetColors ?? undefined}
                    color={this.getValue(data, dataPath)}
                    onChange={color => {
                        this.setState((s: SettingsListState) => {
                            // get previous value before update
                            const prevValue = this.getValue(s.Data, dataPath);
                            const newValue = `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`;

                            this.setValueState(s, dataPath, newValue);
                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);

                            // emit change event
                            const callback = this.props.onElementChanged;
                            if (isFunction(callback))
                                callback(dataPath, newValue, prevValue, s.Data);

                            return s;
                        }, this.autoSave);
                    }} />} />
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    renderBarcodeScanner(key: string, element: SettingsElementBarcodeScanner<any>, groupLevel: number) {
        let pickerKey = element.label;
        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            textStyle={element.oneLine ? this.getTextItemStyleWidth(element) : {}}
            rightItemStyle={element.oneLine ? this.getRightItemStyleWidth(element) : { width: "100%" }}
            rightItemInSeperateLine={!element.oneLine}
            rightItem={this.CreateBarcodeScanner(key, pickerKey, this.state.Data, element)}
        />;
    }
    // --------------------------------------------------------------------------------------------
    private CreateBarcodeScanner(key: string, pickerKey: string, data: any, element: SettingsElementBarcodeScanner<any>): ReactElement<any> {
        let isDisabled = element?.readonly || element?.disable;
        let errorStr = this.state.validationErrors[element.dataPath];
        return <div
            id={"barcode-input-id-" + key}
            key={"barcode-input-key-" + key}
            style={{ width: "100%" }}
        >
            <InputGroup>
                <Input
                    autoFocus={element.autoFocus}
                    readOnly={element.readonly}
                    disabled={isDisabled}
                    value={this.getValue(data, element.dataPath)}
                    onChange={(value) => {
                        this.setState((s: SettingsListState) => {
                            this.setValueState(s, element.dataPath, value);
                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave);
                    }}
                />
                <InputGroup.Button
                    onClick={(event) => {
                        let externalIndentForBarcode = getGlobal("UseExternalBarcodeScanner");
                        if (!IsNullOrWhiteSpace(externalIndentForBarcode)) {
                            // We are going to send a request for opening  native scanner in  mobile view
                            DebugEx.TraceError("We have set for external barcode scanner", externalIndentForBarcode);
                            try {
                                // Set a callback for results
                                (window as any).Yodiwo.BarcodeScannerResults = (success: boolean, data: string) => {
                                    if (!success)
                                        DebugEx.TraceError("Failed to get results with:", data);
                                    else
                                        DebugEx.TraceError("Success results with:", data);
                                    if (success) {
                                        this.setState((state: SettingsListState) => {
                                            // get previous value before update
                                            const prevData = this.getValue(state.Data, element.dataPath);

                                            this.setValueState(state, element.dataPath, data);

                                            // emit element change event
                                            if (isFunction(this.props.onElementChanged)) {
                                                this.props.onElementChanged(element.dataPath, data, prevData, state.Data);
                                            }

                                            return state;
                                        });
                                    }

                                    // Clean cb
                                    (window as any).Yodiwo.BarcodeScannerResults = undefined;
                                };

                                if (_.isFunction(window[externalIndentForBarcode])) {
                                    (window as any)[externalIndentForBarcode]();
                                }
                            } catch (ex) {
                                DebugEx.TraceException(ex);
                            }
                        } else
                            this.setState((s: SettingsListState) => {
                                s.dialogConf = {
                                    title: "Scan",
                                    contentStaticSize: { w: 0.95, h: 0.6 },
                                    actionsFunc: () => {
                                        let buttons = [
                                            <IconButton
                                                key="cancel-btn"
                                                tooltip="Cancel"
                                                iconClassName="fas fa-times"
                                                iconStyle={{ color: red200 }}
                                                onClick={() => this.setState((s: SettingsListState) => {
                                                    this.setValueState(s, element.dataPath, this.getValue(this.state.Data, element.dataPath));
                                                    s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                                    s.dialogConf = null;
                                                    return s;
                                                }, this.autoSave)}
                                            />
                                        ];

                                        // We don't ask the user to save when we send automatically
                                        if (!element.autoSend)
                                            buttons.push(
                                                <IconButton
                                                    key="save-btn"
                                                    tooltip="Save"
                                                    iconClassName="fas fa-cloud-upload-alt"
                                                    onClick={() => this.setState((s: SettingsListState) => {
                                                        this.setValueState(s, element.dataPath, s.tempScannerValue[pickerKey]);
                                                        s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                                        s.dialogConf = null;
                                                        return s;
                                                    }, this.autoSave)}
                                                />);
                                        return buttons;
                                    },
                                    bodyFunc: (w, h) =>
                                        <Suspense fallback={<LazyLoadingAnimation style={{ width: w, height: h }} />}>
                                            <BarcodeScanner
                                                key={key}
                                                WidgetData={{
                                                    hints: element.hints,
                                                    codeFormat: element.codeFormat,
                                                    autoStart: (element.autoStart == null) || element.autoStart,
                                                    autoSend: (element.autoSend == null) || element.autoSend,
                                                    settingElement: true,
                                                    width: w,
                                                    height: h,
                                                    onSuccessfullScan: (res) => {
                                                        this.setState((state: SettingsListState) => {
                                                            // get previous value before update
                                                            const prevData = this.getValue(state.Data, element.dataPath);
                                                            state.tempScannerValue[pickerKey] = res.text;

                                                            if (element.autoClose) {
                                                                this.setValueState(state, element.dataPath, res.text);
                                                                state.UnsavedData = !_.isEqual(state.Data, this.props.Data);
                                                                state.dialogConf = null;
                                                            }

                                                            // emit element change event
                                                            if (isFunction(this.props.onElementChanged)) {
                                                                this.props.onElementChanged(element.dataPath, res.text, prevData, state.Data);
                                                            }
                                                            return state;
                                                        }, this.autoSave)
                                                    }
                                                }} />
                                        </Suspense>
                                }
                                s.tempScannerValue[pickerKey] = this.getValue(data, element.dataPath);
                                return s;
                            });
                    }}
                >
                    <i className="fad fa-scanner" />
                </InputGroup.Button>
            </InputGroup>
            {(errorStr != null) ? <div className="settingslist-error" >{errorStr}</div> : null}
        </div >
    }
    // --------------------------------------------------------------------------------------------
    private closeDialog = () => {
        this.setState((s: SettingsListState) => {
            s.dialogConf = null;
            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
            return s;
        }, this.autoSave);
    }
    // --------------------------------------------------------------------------------------------
    private updateDialogPriv(data) {
        this.setState({ dialogPrivData: data });
    }
    // --------------------------------------------------------------------------------------------
    private renderDialog(key: string, element: SettingsElementDialog<any>, groupLevel: number) {
        let value = _.cloneDeep(this.getValue(this.state.Data, element.dataPath));
        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            rightItem={<div
                key={"Settings-Icon"}
                style={{
                    width: 36, height: 36, lineHeight: "36px",
                    cursor: "pointer",
                    pointerEvents: "auto",
                    textAlign: "center"
                }}
                onClick={() => {
                    // Run  the init
                    var dialogPrivData = (isDefined(element.dialogConf.initPrivData)) ? element.dialogConf.initPrivData(value) : null;
                    this.setState({
                        dialogPrivData: dialogPrivData
                    }, () => {
                        let title;
                        if (isFunction(element.dialogConf.title))
                            title = (element.dialogConf.title as SettingsDialogTitleGetter)(value);
                        else
                            title = element.dialogConf.title;

                        this.setState({
                            dialogConf: {
                                title: title,
                                contentStaticSize: element.dialogConf.contentStaticSize,
                                actionsFunc: () => (!isDefined(element.dialogConf.actionsFunc)) ? null : element.dialogConf.actionsFunc({
                                    rootData: this.state.Data,
                                    data: value,
                                    privData: this.state.dialogPrivData,
                                    closeDialogCB: this.closeDialog,
                                    updateData: (data) =>
                                        this.setState((s: SettingsListState) => {
                                            this.setValueState(s, element.dataPath, data);
                                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                            return s;
                                        }),
                                    updatePrivData: (data) => this.updateDialogPriv(data)
                                }),
                                bodyFunc: (w, h) => element.dialogConf.bodyFunc(w, h, {
                                    rootData: this.state.Data,
                                    data: value,
                                    privData: this.state.dialogPrivData,
                                    closeDialogCB: this.closeDialog,
                                    updateData: (data) =>
                                        this.setState((s: SettingsListState) => {
                                            this.setValueState(s, element.dataPath, data);
                                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                            return s;
                                        }),
                                    updatePrivData: (data) => this.updateDialogPriv(data)
                                })
                            }
                        })
                    });
                }} >
                {element.icon}
            </div>}
        />;
    }
    // --------------------------------------------------------------------------------------------
    private renderIcon(key: string, element: SettingsElementIcon<any>, groupLevel: number) {
        let value = _.cloneDeep(this.getValue(this.state.Data, element.dataPath));
        const disabled = element.readonly || element.disable;
        const inSeperateLine = isDefined(element?.oneLine) ? !element?.oneLine : false;
        return <ListLiteItem
            primaryText={element.label}
            tooltip={element.tooltip}
            key={key}
            groupLevel={groupLevel}
            leftItem={element.leftIcon}
            rightItemInSeperateLine={inSeperateLine}
            rightItem={<IconButton
                key='settings-icon'
                iconStyle={{
                    color: element?.style?.fontColor ?? blue500,
                    width: 32,
                    height: 32,
                    backgroundColor: grey50,
                    borderRadius: "50%",
                    cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                iconClassName={getIconClassName(value)}
                disabled={disabled}
                onClick={() => {
                    let value = _.cloneDeep(this.getValue(this.state.Data, element.dataPath));
                    this.updateDialogPriv(isDefined(value) ? value : '');
                    this.setState({
                        dialogConf: {
                            title: "Select Icon",
                            contentStaticSize: { w: 0.5, h: 0.9 },
                            actionsFunc: () => [<IconButton
                                key="save-btn"
                                tooltip="Save"
                                iconClassName="fas fa-cloud-upload-alt"
                                onClick={() => {
                                    this.setState((s: SettingsListState) => {
                                        this.setValueState(s, element.dataPath, s.dialogPrivData);
                                        s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                        return s;
                                    }, this.autoSave);
                                    this.closeDialog();
                                }}
                            />,
                            <IconButton
                                key="cancel-btn"
                                tooltip="Cancel"
                                iconClassName="fas fa-times"
                                iconStyle={{ color: red200 }}
                                onClick={this.closeDialog}
                            />],
                            bodyFunc: (w, h) => <Suspense fallback={<LazyLoadingAnimation />}>
                                <IconSelector
                                    defaultIcon={{ className: value }}
                                    width={w}
                                    height={h}
                                    onIconSelection={(iconInfo: IconInfo) => {
                                        this.updateDialogPriv(isDefined(iconInfo) ? iconInfo.className : '')
                                    }}
                                />
                            </Suspense>
                        }
                    });
                }} />}
        />;
    }
    // --------------------------------------------------------------------------------------------
    private _handleFileUpload(dataPath: string, value: string, fileInterceptor: (value: string) => Promise<string>, errorMessage: string) {
        // check if file reader error occurred
        if (isDefined(errorMessage)) {
            this.setState((s: SettingsListState) => {
                s.uploadingFile = false;
                s.uploadFileErrorMessage[dataPath] = errorMessage;
                return s;
            });
        }
        else {
            // check if any value interceptor
            if (isDefined(fileInterceptor)) {
                RunSafe(() => {
                    fileInterceptor(value)
                        .then((finalValue) => {
                            this.setState((s: SettingsListState) => {
                                s.uploadingFile = false;
                                s.uploadFileErrorMessage[dataPath] = "";
                                this.setValueState(s, dataPath, finalValue);
                                s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                return s;
                            }, this.autoSave);
                        })
                        .catch((reason: Error) => {
                            this.setState((s: SettingsListState) => {
                                s.uploadingFile = false;
                                s.uploadFileErrorMessage[dataPath] = "Error reading file";
                                return s;
                            });
                            DebugEx.TraceError(reason);
                        });
                });
            }
            else {
                this.setState((s: SettingsListState) => {
                    s.uploadingFile = false;
                    s.uploadFileErrorMessage[dataPath] = "";
                    this.setValueState(s, dataPath, value);
                    s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                    return s;
                }, this.autoSave);
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private renderImageFileInput(key: string, element: SettingsElementImageFileInput<any>, groupLevel: number) {
        let value = this.getValue(this.state.Data, element.dataPath);
        value = isDefined(value) ? _.cloneDeep(value) : "";

        const uploadError = this.state.uploadFileErrorMessage[element.dataPath];
        const validationError = this.state.validationErrors[element.dataPath];
        const disabled = element.readonly || element.disable;
        const indent = groupLevel + 1;
        const elementId = `FileInput-${element.dataPath}`;
        const style: CSSProperties = {
            marginLeft: indent * 16,
            marginRight: 16,
            fontSize: 14
        };
        return <ListItem
            initiallyOpen
            key={key}
            style={style}
            primaryText={element.label}
            nestedItemsStyle={style}
            nestedItems={[
                <div key='img-input' style={{ display: 'flex', justifyContent: "space-between", padding: '4px 0' }}>
                    {element.previewImg == true
                        ? <img
                            key='img-tag'
                            style={{ objectFit: 'contain', maxHeight: 48, borderRadius: 4 }}
                            src={isStringEmpty(value) ? "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==" : value} />
                        : null}
                    {!disabled ?
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <label
                                htmlFor={elementId}
                                style={{ marginBottom: 0 }}>
                                <ToolbarIconButton
                                    style={{
                                        padding: 10,
                                        cursor: "pointer"
                                    }}
                                    tooltip="Choose image"
                                    className="fas fa-arrow-alt-from-bottom" />
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                id={elementId}
                                name={elementId}
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    var input = e.target as HTMLInputElement;
                                    var files = input.files;
                                    console.error(input, files);
                                    if (files && files.length > 0) {
                                        var FR = new FileReader();
                                        FR.onload = (eFR: ProgressEvent) => {
                                            if (element.imgSettings == null) {
                                                this._handleFileUpload(element.dataPath, FR.result as string, element.fileInterceptor, null);
                                            } else {
                                                DomUtils.getResizedImage(
                                                    FR.result as string,
                                                    element.imgSettings.maxWidth ?? 1920,
                                                    element.imgSettings.maxHeight ?? 1200,
                                                    element.imgSettings.format ?? 'jpeg',
                                                    element.imgSettings.quality ?? 0.85
                                                ).then(value => {
                                                    this._handleFileUpload(element.dataPath, value.src, element.fileInterceptor, null);
                                                });
                                            }
                                        };
                                        FR.onerror = (eFR: ProgressEvent) => {
                                            this._handleFileUpload(element.dataPath, null, null, FR.error.message);
                                        };

                                        this.setState({ uploadingFile: true });

                                        FR.readAsDataURL(files.item(0));
                                    }
                                }} />
                            {IsNullOrWhiteSpace(value)
                                ? null
                                : <ToolbarIconButton
                                    tooltip="Clear image"
                                    className="fas fa-times"
                                    style={{
                                        padding: 10,
                                        cursor: "pointer"
                                    }}
                                    onClick={() => {
                                        this.setState((s: SettingsListState) => {
                                            this.setValueState(s, element.dataPath, "");
                                            s.uploadFileErrorMessage[element.dataPath] = "";
                                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                            return s;
                                        }, this.autoSave);
                                    }} />}
                            {!IsNullOrWhiteSpace(value) && element.acceptButton == true
                                ? <ToolbarIconButton
                                    tooltip="Accept image"
                                    className="fas fa-check"
                                    style={{
                                        padding: 10,
                                        cursor: "pointer"
                                    }}
                                    onClick={() => {
                                        if (isFunction(element.acceptButtonFunc))
                                            element.acceptButtonFunc(value, this.state.Data);

                                        if (isFunction(this.props.onElementChanged)) {
                                            this.props.onElementChanged(
                                                element.dataPath,
                                                value,
                                                '',
                                                this.state.Data);
                                        }
                                    }} />
                                : null}
                        </div>
                        : null}
                </div>,
                !isStringEmpty(uploadError) ?
                    <div style={{ textAlign: 'right' }}>
                        <span
                            style={{
                                color: red500,
                                fontWeight: 500,
                                marginRight: 15
                            }}>
                            {uploadError}</span>
                    </div>
                    : null,
                element.externalURL
                    ? <TextBox
                        fullWidth
                        type='text'
                        key={key}
                        disabled={disabled || value.length > 256}
                        style={{ color: 'inherit' }}
                        errorStyle={{ color: orange500 }}
                        value={value.length > 256 ? "" : value}
                        onChangeString={newValue =>
                            this.setState((s: SettingsListState) => {
                                this.setValueState(s, element.dataPath, newValue);
                                s.uploadFileErrorMessage[element.dataPath] = "";
                                s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                                return s;
                            }, this.autoSave)
                        } />
                    : null,
                validationError != null ? <div className='settingslist-error'>{validationError}</div> : null
            ]} />;
    }
    // --------------------------------------------------------------------------------------------
    renderAttachments = (key: string, element: SettingsElementAttachments<any>, indentation: number) => {
        let disabled = element.readonly || element.disable;

        let labelElement = null;
        if (!IsNullOrWhiteSpace(element.label)) {
            labelElement = <div title={element.tooltip}>{element.label}</div>;
        }

        let fileInputLabel = 'Add attachment...';
        if (!IsNullOrWhiteSpace(element.fileInputLabel)) {
            fileInputLabel = element.fileInputLabel;
        }

        // file-select field
        let unuploadability = disabled || element.uploadable == false || this.state.uploadingAttachment;
        let uploadElement = (
            <>
                <label
                    className={unuploadability ? 'cannot-upload' : null}
                    htmlFor={element.dataPath}
                    style={{
                        cursor: unuploadability ? 'initial' : 'pointer',
                        fontWeight: 'inherit'
                    }}>
                    <span style={{
                        padding: 2,
                        ...GetReactStyle(element.fileInputStyle)
                    }}>
                        {fileInputLabel}
                    </span>
                    {this.state.uploadingAttachment
                        ? <i className='fas fa-spinner' style={{ padding: 2 }} />
                        : null}
                </label>
                {/* browse file */}
                <input
                    id={element.dataPath}
                    type='file'
                    style={{ display: 'none' }}
                    disabled={unuploadability}
                    onChange={changeEvent => {
                        // get files
                        let inputElement = changeEvent.target as HTMLInputElement;
                        let files = inputElement.files;

                        if (files?.length > 0) {
                            let file = files.item(0);
                            let fileReader = new FileReader();

                            fileReader.onload = () => {
                                this.setState((state: SettingsListState) => {
                                    let result = fileReader.result as string;
                                    let attachment: IAttachment = {
                                        id: getRandomKey(),
                                        name: file.name,
                                        src: result,
                                        isImage: result.startsWith('data:image/'),
                                        info: null
                                    };

                                    // add attachment
                                    let attachments: IAttachment[] = _.cloneDeep(this.getValue(state.Data, inputElement.id));
                                    attachments?.push(attachment);
                                    this.setValueState(state, inputElement.id, attachments);

                                    // emit event
                                    if (isFunction(this.props.onButtonPressed)) {
                                        let action: IAttachmentAction = {
                                            action: eAttachmentAction.upload,
                                            dataPath: inputElement.id,
                                            attachment: attachment
                                        };
                                        this.props.onButtonPressed(action, state.Data);
                                    }

                                    inputElement.value = '';
                                    return state;
                                }, this.autoSave);
                            };
                            //fileReader.onprogress = progressEvent => { };
                            fileReader.onloadstart = () => this.setState({ uploadingAttachment: true });
                            fileReader.onloadend = () => this.setState({ uploadingAttachment: false });
                            fileReader.onerror = () => this.setState({ uploadingAttachment: false });
                            fileReader.onabort = () => this.setState({ uploadingAttachment: false });
                            fileReader.readAsDataURL(file);
                        }
                    }} />
            </>
        );

        let attachments: IAttachment[] = this.getValue(this.state.Data, element.dataPath);
        return (
            <div
                key={key}
                className='attachments'
                style={{ marginLeft: (indentation + 1) * 16 }}>
                {labelElement}
                {uploadElement}
                <div>
                    {/* attachments boxes */}
                    {_.map(attachments, attachment => {
                        let boxContent = null;

                        if (!attachment.isImage) {
                            // show name and thumb file
                            boxContent = (
                                <div className='attachment-content'>
                                    <span
                                        className='fas fa-file'
                                        style={{
                                            fontSize: 24,
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            opacity: 0.2,
                                            transform: 'translate(-50%, -50%)'
                                        }} />
                                    <span style={{
                                        position: 'absolute',
                                        color: '#1f2326',
                                        fontSize: 12
                                    }}>
                                        {attachment.name}
                                    </span>
                                </div>
                            );
                        } else {
                            // just an image
                            boxContent = (
                                <div className='attachment-content'>
                                    <img
                                        width='100%'
                                        height='100%'
                                        alt={attachment.name}
                                        src={attachment.src}
                                    />
                                </div>
                            );
                        }

                        let boxOverlay = null;
                        if (!disabled) {
                            let iconStyle: CSSProperties = {
                                fontWeight: 'inherit',
                                margin: '0 4px',
                                cursor: 'pointer'
                            };

                            // when pen icon is pressed
                            const editAttachmentHandler = () => {
                                this.setState({
                                    editedAttachmentName: attachment.name,
                                    dialogConf: {
                                        title: 'Edit Attachment',
                                        bodyFunc: (width, height) => {
                                            return (
                                                <div style={{
                                                    width: width,
                                                    height: height,
                                                    padding: '24px 12px'
                                                }}>
                                                    <label>{'Name'}</label>
                                                    <Input
                                                        type='text'
                                                        defaultValue={this.state.editedAttachmentName}
                                                        onChange={value => {
                                                            // update name
                                                            this.setState({ editedAttachmentName: value });
                                                        }} />
                                                </div>
                                            );
                                        },
                                        actions: [
                                            <RsButton
                                                key='save'
                                                appearance='primary'
                                                size='sm'
                                                style={{ margin: '0 4px' }}
                                                onClick={() => {
                                                    this.setState((state: SettingsListState) => {
                                                        // find attachment
                                                        let editedAttachment = _.find(attachments, value => {
                                                            return value.id == attachment.id;
                                                        });

                                                        if (editedAttachment != undefined) {
                                                            // update name
                                                            editedAttachment.name = state.editedAttachmentName;
                                                            this.setValueState(state, element.dataPath, attachments);

                                                            // emit event
                                                            if (isFunction(this.props.onButtonPressed)) {
                                                                let action: IAttachmentAction = {
                                                                    action: eAttachmentAction.edit,
                                                                    dataPath: element.dataPath,
                                                                    attachment: editedAttachment
                                                                };
                                                                this.props.onButtonPressed(action, state.Data);
                                                            }
                                                        }

                                                        state.dialogConf = null;
                                                        state.editedAttachmentName = null;
                                                        return state;
                                                    }, this.autoSave);
                                                }}>
                                                {'Save'}
                                            </RsButton>,
                                            <RsButton
                                                key='cancel'
                                                appearance='default'
                                                size='sm'
                                                style={{ margin: '0 4px' }}
                                                onClick={this.closeDialog}>
                                                {'Cancel'}
                                            </RsButton>
                                        ]
                                    }
                                });
                            };

                            // when trash icon is pressed
                            const deleteAttachmentHandler = () => {
                                this.setState({
                                    dialogConf: {
                                        bodyFunc: (width, height) => {
                                            return (
                                                <div style={{
                                                    width: width,
                                                    height: height,
                                                    padding: '24px 12px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {'Are you sure you want to delete attachment "' + attachment.name + '"?'}
                                                </div>
                                            );
                                        },
                                        actions: [
                                            <RsButton
                                                appearance='primary'
                                                size='sm'
                                                style={{ margin: '0 4px' }}
                                                onClick={() => {
                                                    this.setState((state: SettingsListState) => {
                                                        // delete attachment
                                                        _.remove(attachments, value => {
                                                            return value.id == attachment.id;
                                                        });
                                                        this.setValueState(state, element.dataPath, attachments);

                                                        // emit event
                                                        if (isFunction(this.props.onButtonPressed)) {
                                                            let action: IAttachmentAction = {
                                                                action: eAttachmentAction.delete,
                                                                dataPath: element.dataPath,
                                                                attachment: attachment
                                                            };
                                                            this.props.onButtonPressed(action, state.Data);
                                                        }

                                                        state.dialogConf = null;
                                                        return state;
                                                    }, this.autoSave);
                                                }}>
                                                {'Delete'}
                                            </RsButton>,
                                            <RsButton
                                                appearance='default'
                                                size='sm'
                                                style={{ margin: '0 4px' }}
                                                onClick={this.closeDialog}>
                                                {'Cancel'}
                                            </RsButton>
                                        ]
                                    }
                                });
                            };

                            // show actions on hover
                            boxOverlay = (
                                <div
                                    className='attachment-overlay'
                                    style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0
                                    }}>
                                    {/* download attachment */}
                                    {element.downloadable == false
                                        ? null
                                        : <a href={attachment.src}
                                            target='_blank'
                                            download={attachment.name}
                                            style={{ color: 'inherit' }}>
                                            <span
                                                className='fas fa-file-download'
                                                title='Download'
                                                style={iconStyle} />
                                        </a>}
                                    {/* edit attachment */}
                                    {element.editable == false
                                        ? null
                                        : <span
                                            className='fas fa-pen'
                                            title='Edit'
                                            style={iconStyle}
                                            onClick={editAttachmentHandler} />}
                                    {/* delete attachment */}
                                    {element.deletable == false
                                        ? null
                                        : <span
                                            className='fas fa-trash'
                                            title='Delete'
                                            style={iconStyle}
                                            onClick={deleteAttachmentHandler} />}
                                </div>
                            );
                        }

                        return (
                            <div
                                className='attachment-box'
                                style={{
                                    width: 94,
                                    height: 62,
                                    margin: 2,
                                    padding: 2,
                                    wordBreak: 'break-all',
                                    position: 'relative',
                                    display: 'inline-block',
                                    color: '#737577',
                                    backgroundColor: '#f7f9fa',
                                    border: '1px solid #dfe5eb',
                                    overflow: 'hidden',
                                    verticalAlign: 'top'
                                }}>
                                {boxContent}
                                {boxOverlay}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
    // --------------------------------------------------------------------------------------------
    renderRadioButtons(key: string, element: SettingsElementRadioButtons<any>, groupLevel: number) {
        const errorStr = this.state.validationErrors[element.dataPath];
        const currentValue = this.getValue(this.state.Data, element.dataPath);

        const radioButtons = (
            <div style={{ width: '100%' }}>
                <RadioGroup
                    appearance={element.appearance}
                    inline={element.radioInline}
                    value={currentValue}
                    onChange={newValue => {
                        if (!element.readonly) {
                            this.setState((state: SettingsListState) => {
                                // get previous value before update
                                const prevData = this.getValue(state.Data, element.dataPath);
                                this.setValueState(state, element.dataPath, newValue);

                                // emit change event
                                const callback = this.props.onElementChanged;
                                if (isFunction(callback))
                                    callback(element.dataPath, newValue, prevData, state.Data);

                                state.UnsavedData = !_.isEqual(state.Data, this.props.Data);
                                return state;
                            }, this.autoSave);
                        }
                    }}>
                    {/* item to radio */}
                    {_.map(element.items, (item, index) => {
                        return (
                            <Radio
                                key={`radio-${index}`}
                                disabled={element.disable}
                                value={item.value as any}>
                                {item.label}
                            </Radio>);
                    })}
                </RadioGroup>
                {isDefined(errorStr) ? <div className='settingslist-error'>{errorStr}</div> : null}
            </div>
        );
        return (
            <div key={key}>
                <ListLiteItem
                    groupLevel={groupLevel}
                    primaryText={element.label}
                    tooltip={element.tooltip}
                    textStyle={element.oneLine ? this.getTextItemStyleWidth(element) : {}}
                    leftItem={element.leftIcon}
                    rightItem={radioButtons}
                    rightItemInSeperateLine={!element.oneLine}
                    rightItemStyle={element.oneLine ? this.getRightItemStyleWidth(element) : { width: '100%' }}
                />
            </div>
        );
    }
    // --------------------------------------------------------------------------------------------
    private _showTooltip(msg: string, x: number, y: number) {
        // Show tooltip with information.
        this.setState({
            tooltip: {
                open: true,
                content: msg,
                xPos: x,
                yPos: y
            }
        });
    }
    // --------------------------------------------------------------------------------------------
    private _hideTooltip = () => {
        // Hide and reset tooltip.
        this.setState({
            tooltip: {
                open: false,
                content: '',
                xPos: 0,
                yPos: 0
            }
        });
    }
    // --------------------------------------------------------------------------------------------
    private renderItemsMgmtDialog(key: string, element: SettingsElementItemsMgmtDialog<any>, groupLevel: number) {
        return <SettingsListItemsDialog
            key={key}
            Key={key}
            element={element}
            Data={this.state.Data}
            DataPath={element.dataPath}
            isListItem={true}
            startOpen={false}
            groupLevel={groupLevel}
            onSaveData={(
                itemsMgmt: any[],
                itemsToolbarToggleState: { [key: string]: boolean },
                itemsToolbarDropdownState: { [key: string]: string | number }) => {
                this.setState((s: SettingsListState) => {
                    this.setValueState(
                        s,
                        element.dataPath,
                        itemsMgmt
                    );
                    _.forEach(element.toolbarToggles, t => {
                        this.setValueState(s, t.dataPath, itemsToolbarToggleState[t.dataPath]);
                    });
                    _.forEach(element.toolbarDropdowns, d => {
                        this.setValueState(s, d.dataPath, itemsToolbarDropdownState[d.dataPath]);
                    });
                    if (isFunction(element.onSaveUpdate)) {
                        s.Data = element.onSaveUpdate(s.Data);
                    }
                    s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                    return s;
                }, this.autoSave)
            }}
            showTooltip={(msg, x, y) => this._showTooltip(msg, x, y)}
            hideTooltip={() => this._hideTooltip()}
        />
    }
    // --------------------------------------------------------------------------------------------
    renderListItem(key: string, element: SettingsElementListItem<any>, groupLevel: number) {
        let rightIconButton: ReactElement<any> = !isFunction(element.rightIconButtonsFunc) ? null : element.rightIconButtonsFunc(this.state.Data, (newData) => {
            this.setState((s: SettingsListState) => {
                s.Data = newData;
                s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
            }, this.autoSave);
        });

        return <ListItem
            key={key}
            style={{ marginLeft: (groupLevel > 0) ? groupLevel * 16 : undefined, color: element?.style?.fontColor }}
            primaryText={element.label}
            secondaryText={isDefined(element.secondaryText) ? element.secondaryText : null}
            secondaryTextStyle={GetReactStyle(element.secondaryTextStyle)}
            leftIcon={isDefined(element.leftIcon) ? element.leftIcon : null}
            rightIcon={rightIconButton}
        />;
    }
    // --------------------------------------------------------------------------------------------
    validateElements(dataPaths: string[] = null, suppressErrors: boolean = false): boolean {
        const hasSpecificDataPaths = _.isArray(dataPaths) && dataPaths.length > 0;
        const getElements = (x: SettingsElementGroup<T> | SettingsGroup<T>) => {
            if (x.elements != null)
                return x.elements;
            if (x.elementsFunc != null)
                return x.elementsFunc(this.state.Data)
            return [];
        };

        let elements: SettingsElementAll<any>[] = [];
        if (hasSpecificDataPaths) {
            // keep specific data paths
            elements = _(this.props.SettingsMap)
                .flatMapDeep(getElements)
                .compact()
                .flatMapDeep(x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x)
                .flatMapDeep(x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x)
                .flatMapDeep(x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x)
                .filter(x => _.includes(dataPaths, x.dataPath))
                .value();
        } else {
            // collect any mandatory, email and ip elements (4 level deep)
            elements = _(this.props.SettingsMap)
                .flatMapDeep(getElements)
                .compact()
                .flatMapDeep(x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x)
                .flatMapDeep(x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x)
                .flatMapDeep(x => (x.type == SettingsType.GroupStr || x.type == SettingsType.Group) ? getElements(x) : x)
                .filter(x => x.mandatory ||
                    ((x.type == SettingsType.String || x.type == SettingsType.StringStr) && (x.fieldType == 'email' || x.fieldType == 'ip')) ||
                    (!IsNullOrWhiteSpace(x.validationPattern) && !IsNullOrWhiteSpace(x.validationErrorMsg))
                )
                .value();
        }

        let errors: { [key: string]: string } = {};
        let valid = true;
        for (let el of elements) {
            const value = _.get(this.state.Data, el.dataPath);

            // TODO: check the value based on element type
            if ((el.mandatory || hasSpecificDataPaths) &&
                (value == null || (_.isString(value) && IsNullOrWhiteSpace(value)))) {
                valid = false;
                errors[el.dataPath] = 'This field is required';
            }
            else if (!IsNullOrWhiteSpace(el.validationPattern)) {
                // Generic validation based on user pattern
                try {
                    let regex = RegExp(el.validationPattern);
                    if (!regex.test(value)) {
                        valid = false;
                        errors[el.dataPath] = el.validationErrorMsg;
                    }
                } catch (ex) {
                    DebugEx.TraceError(ex);
                }
            } else if (el.type == SettingsType.String || el.type == SettingsType.StringStr) {
                if (el.fieldType == 'email') {
                    if (!emailValidationPattern.test(value)) {
                        valid = false;
                        if (!IsNullOrWhiteSpace(el.validationErrorMsg))
                            errors[el.dataPath] = el.validationErrorMsg;
                        else
                            errors[el.dataPath] = 'Invalid email address';
                    }
                } else if (el.fieldType == 'ip') {
                    if (!ipValidationPattern.test(value)) {
                        valid = false;
                        if (!IsNullOrWhiteSpace(el.validationErrorMsg))
                            errors[el.dataPath] = el.validationErrorMsg;
                        else
                            errors[el.dataPath] = 'Invalid IP address';
                    }
                }
            }
        }

        if (!suppressErrors)
            this.setState({ validationErrors: errors });
        return valid;
    }
    // --------------------------------------------------------------------------------------------
    renderButton(key: string, element: SettingsElementButton<any>, groupLevel: number) {
        let isAdvanced = isDefined(element.advancedTooltip) || isDefined(element.advancedPopup);
        let isDisabled = element.readonly || element.disable;

        // disable if mandatory dependencies are not filled
        let mandatoryDataPaths = null;
        if (_.isArray(element.mandatoryDataPaths) && element.mandatoryDataPaths.length > 0) {
            mandatoryDataPaths = element.mandatoryDataPaths;
            isDisabled = !this.validateElements(mandatoryDataPaths, true);
        }

        // normal or list-item?
        if (element.layout == 'list-item') {
            const inSeperateLine = isDefined(element.oneLine) ? !element.oneLine : false;
            const button = (
                <Button
                    key={key + '-button'}
                    label={null}
                    labelAutoSize={false}
                    tooltip={element.tooltip}
                    icon={element.icon}
                    iconType={element.iconType}
                    iconFontSize={element.iconSize}
                    iconWidth={element.iconWidth}
                    iconFontColor={element.fontColor}
                    borderRadius={element.borderRadius ?? 100}
                    shadowLevel={element.shadowLevel}
                    disabled={isDisabled}
                    style={{
                        width: 40,
                        minWidth: element.width ?? 40,
                        height: element.height ?? 40,
                        minHeight: element.height ?? 40,
                        color: element.fontColor,
                        backgroundColor: element.backgroundColor,
                        margin: 0,
                        paddingTop: 2,
                        paddingBottom: 2,
                        textTransform: element.uppercase ? 'uppercase' : 'initial',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                    onClick={isDisabled ? _.noop : () => {
                        if (isFunction(this.props.onButtonPressed)
                            && (element.forceSend || this.validateElements(mandatoryDataPaths))
                            && (!isAdvanced || element.splitButton)) {
                            this.props.onButtonPressed(element, this.state.Data);
                        }
                    }}
                    splitButton={element.splitButton}
                    advancedTooltip={element.advancedTooltip}
                    advancedPopup={element.advancedPopup}
                    buttonPressed={(element, data) => {
                        // emit button press event
                        if (isFunction(this.props.onButtonPressed)) {
                            this.props.onButtonPressed(element, data);
                        }
                    }}
                    elementChanged={(dataPath, data, prevData, formData) => {
                        // emit element change event
                        if (isFunction(this.props.onElementChanged)) {
                            this.props.onElementChanged(dataPath, data, prevData, formData);
                        }
                    }} />
            );

            return (
                <ListLiteItem
                    key={key}
                    groupLevel={groupLevel}
                    primaryText={element.label}
                    secondaryText={element.secondaryLabel}
                    tooltip={element.tooltip}
                    leftItem={element.leftIcon}
                    rightItemInSeperateLine={inSeperateLine}
                    rightItem={button} />
            );
        } else {
            return (
                <Button
                    key={key}
                    label={element.label}
                    labelPosition={element.labelPosition}
                    labelAutoSize={false}
                    tooltip={element.tooltip}
                    icon={element.icon}
                    iconType={element.iconType}
                    iconFontSize={element.iconSize}
                    iconWidth={element.iconWidth}
                    iconFontColor={element.style?.fontColor ?? element.fontColor}
                    borderRadius={element.borderRadius}
                    shadowLevel={element.shadowLevel}
                    disabled={isDisabled}
                    style={{
                        width: element.width ?? undefined,
                        height: element.height ?? undefined,
                        minHeight: element.height ?? 40,
                        color: element.style?.fontColor ?? element.fontColor,
                        backgroundColor: element.style?.backgroundColor ?? element.backgroundColor,
                        marginLeft: (groupLevel > 0 ? groupLevel * 16 : 0) + 10,
                        marginRight: 10,
                        margin: element.margin ?? undefined,
                        paddingTop: 2,
                        paddingBottom: 2,
                        textTransform: element.uppercase ? 'uppercase' : 'initial',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                    styleLabel={{
                        textAlign: element.textAlign ?? 'center'
                    }}
                    onClick={isDisabled ? _.noop : () => {
                        if (isFunction(this.props.onButtonPressed)
                            && (element.forceSend || this.validateElements(mandatoryDataPaths))
                            && (!isAdvanced || element.splitButton)) {
                            this.props.onButtonPressed(element, this.state.Data);
                        }
                    }}
                    splitButton={element.splitButton}
                    advancedTooltip={element.advancedTooltip}
                    advancedPopup={element.advancedPopup}
                    buttonPressed={(element, data) => {
                        // emit button press event
                        if (isFunction(this.props.onButtonPressed)) {
                            this.props.onButtonPressed(element, data);
                        }
                    }}
                    elementChanged={(dataPath, data, prevData, formData) => {
                        // emit element change event
                        if (isFunction(this.props.onElementChanged)) {
                            this.props.onElementChanged(dataPath, data, prevData, formData);
                        }
                    }} />
            );
        }
    }
    // --------------------------------------------------------------------------------------------
    renderButtonGroup(key: string, element: SettingsElementButtonGroup<any>, indentation: number) {
        // add label
        let labelElement = null;
        if (!IsNullOrWhiteSpace(element.label)) {
            labelElement = <div title={element.tooltip}>{element.label}</div>;
        }

        // compact view if elements are next to each other
        let compactView = false;

        // Remove empty buttons
        let buttons = _.compact(element.buttons);
        if (buttons?.length > 1) {
            let widthButtons = _.filter(buttons, button => {
                return isDefined(button.width);
            });
            compactView = widthButtons.length < buttons.length
                || (element.justifyContent != 'space-between' &&
                    element.justifyContent != 'space-around' &&
                    element.justifyContent != 'space-evenly');
        }

        // map to primitive buttons
        const buttonElements = _.map(buttons, (button, index) => {
            let isAdvanced = isDefined(button.advancedTooltip) || isDefined(button.advancedPopup);
            let isDisabled = button.readonly || button.disable || element.disable || element.readonly;

            // disable if mandatory dependencies are not filled
            let mandatoryDataPaths = null;
            if (_.isArray(button.mandatoryDataPaths) && button.mandatoryDataPaths.length > 0) {
                mandatoryDataPaths = button.mandatoryDataPaths;
                isDisabled = !this.validateElements(mandatoryDataPaths, true);
            }

            let borderRadius = button.borderRadius;
            let shadowLevel = button.shadowLevel;

            if (compactView) {
                // add rounded corners to group
                if (index == 0) {
                    if (isDefined(borderRadius)) {
                        borderRadius = `${borderRadius}px 0px 0px ${borderRadius}px`;
                    } else {
                        borderRadius = '4px 0px 0px 4px';
                    }
                } else if (index == buttons.length - 1) {
                    if (isDefined(borderRadius)) {
                        borderRadius = `0px ${borderRadius}px ${borderRadius}px 0px`
                    } else {
                        borderRadius = '0px 4px 4px 0px';
                    }
                } else {
                    borderRadius = 0;
                }
                shadowLevel = 0;
            }

            return (
                <Button
                    key={`${key}-grouped-button-${index}`}
                    width={button.width ?? '100%'}
                    height={button.height ?? 40}
                    disabled={isDisabled}
                    label={button.label}
                    labelPosition={button.labelPosition}
                    labelAutoSize={false}
                    tooltip={button.tooltip}
                    icon={button.icon}
                    iconType={button.iconType}
                    iconFontSize={button.iconSize}
                    iconWidth={button.iconWidth}
                    iconFontColor={button.style?.fontColor ?? button.fontColor}
                    borderRadius={borderRadius}
                    shadowLevel={shadowLevel}
                    style={{
                        minWidth: button.width ?? 40,
                        minHeight: button.height ?? 40,
                        margin: button.margin,
                        fontSize: button.fontSize,
                        color: button.fontColor,
                        backgroundColor: button.backgroundColor,
                        textTransform: button.uppercase ? 'uppercase' : 'initial',
                        ...GetReactStyle(button.style)
                    }}
                    styleLabel={{ textAlign: button.textAlign }}
                    onClick={isDisabled ? _.noop : () => {
                        if (isFunction(this.props.onButtonPressed)
                            && (button.forceSend || this.validateElements(mandatoryDataPaths))
                            && (!isAdvanced || button.splitButton)) {
                            this.props.onButtonPressed(button, this.state.Data);
                        }
                    }}
                    splitButton={button.splitButton}
                    advancedTooltip={button.advancedTooltip}
                    advancedPopup={button.advancedPopup}
                    buttonPressed={(button, data) => {
                        // emit button press event
                        if (isFunction(this.props.onButtonPressed)) {
                            this.props.onButtonPressed(button, data);
                        }
                    }}
                    elementChanged={(dataPath, data, prevData, formData) => {
                        // emit element change event
                        if (isFunction(this.props.onElementChanged)) {
                            this.props.onElementChanged(dataPath, data, prevData, formData);
                        }
                    }} />
            );
        });

        // wrap header and buttons
        return (
            <div key={key} style={{ margin: `0px ${(indentation + 1) * 16}px` }}>
                {labelElement}
                <div
                    key={`${key}-button-group`}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: element.justifyContent
                    }}>
                    {buttonElements}
                </div>
            </div>
        );
    }
    // --------------------------------------------------------------------------------------------
    renderMarkdownString(key: string, element: SettingsElementMarkdownString<any>, groupLevel: number) {
        let icon = <i className="fas fa-pen" style={{ fontSize: 16, color: element?.style?.fontColor }} />;
        if (isDefined(element.icon))
            if (isString(element.icon))
                icon = <i className={element.icon} style={{ color: element?.style?.fontColor }} />;
            else
                icon = element.icon as ReactElement;

        let dialogElement: SettingsElementDialog<any> = {
            label: element.label,
            type: SettingsType.Dialog,
            dataPath: element.dataPath,
            icon: icon,
            tooltip: element.tooltip,
            dialogConf: {
                contentStaticSize: { w: 0.85, h: 0.95 },
                bodyFunc: (w, h, di) => {
                    return <MarkdownEditor
                        width={w}
                        height={h}
                        Text={di.data}
                        readOnly={element?.readonly || element?.disable}
                        Title={element.label}
                        onCloseRequest={() => di.closeDialogCB()}
                        onSave={(newData) => di.updateData(newData)}
                    />;
                }
            }
        };
        return this.renderDialog(key, dialogElement, groupLevel);
    }
    // --------------------------------------------------------------------------------------------
    renderCodeEditor(key: string, element: SettingsElementCode<any>, groupLevel: number) {
        let icon = <i className="fas fa-pen" style={{ fontSize: 16, color: element?.style?.fontColor }} />;
        if (isDefined(element.icon))
            if (isString(element.icon))
                icon = <i className={element.icon} style={{ color: element?.style?.fontColor }} />;
            else
                icon = element.icon as ReactElement;

        let dialogElement: SettingsElementDialog<any> = {
            label: element.label,
            type: SettingsType.Dialog,
            dataPath: element.dataPath,
            icon: icon,
            dialogConf: {
                title: "",
                initPrivData: (value) => value,
                contentStaticSize: { w: 0.75, h: 0.95 },
                actionsFunc: () => [<IconButton
                    key="save-btn"
                    tooltip="Save"
                    iconClassName="fas fa-cloud-upload-alt"
                    onClick={() => {
                        this.setState((s: SettingsListState) => {
                            this.setValueState(s, element.dataPath, s.dialogPrivData);
                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave);
                        this.closeDialog();
                    }}
                />,
                <IconButton
                    key="cancel-btn"
                    tooltip="Cancel"
                    iconClassName="fas fa-times"
                    iconStyle={{ color: red200 }}
                    onClick={this.closeDialog}
                />],
                bodyFunc: (w, h, di) => {
                    return <MonacoEditor
                        key="dialog-editor"
                        width={w}
                        height={h}
                        value={di.privData}
                        options={{
                            selectOnLineNumbers: true,
                            readOnly: element.readonly || element.disable || false,
                        }}
                        onChange={(newData) => di.updatePrivData(newData)}
                        language={element.codeType}
                        theme='vs-dark'
                    />;
                }
            }
        };
        return this.renderDialog(key, dialogElement, groupLevel);
    }
    //---------------------------------------------------------------------------------------------
    renderTextEditorString(key: string, element: SettingsElementTextEditorString<any>, groupLevel: number) {
        let dialogElement: SettingsElementDialog<any> = {
            label: element.label,
            type: SettingsType.Dialog,
            dataPath: element.dataPath,
            icon: (element.icon == null) ? <i className="fas fa-pen" style={{ fontSize: 16, color: element?.style?.fontColor }} /> : element.icon,
            dialogConf: {
                contentStaticSize: { w: 0.8, h: 0.95 },
                bodyFunc: (w, h, di) => {
                    return <Suspense fallback={<LazyLoadingAnimation />}>
                        <TextEditorSettingsEditor
                            width={w}
                            height={h}
                            Text={di.data}
                            readOnly={element.readonly || element.disable}
                            Title={element.label}
                            onCloseRequest={() => di.closeDialogCB()}
                            onSave={(newData) => di.updateData(newData)}
                        />
                    </Suspense>
                }
            }
        };
        return this.renderDialog(key, dialogElement, groupLevel);
    }
    // --------------------------------------------------------------------------------------------
    renderTabsGroup(key: string, element: SettingsElementTabsGroup<any>, groupLevel: number, groupId: string) {
        // Select appearance 
        let appearance: 'tabs' | 'subtle' = 'subtle'
        switch (element.appearance) {
            case "line": appearance = 'subtle'; break;
            case "tabs": appearance = 'tabs'; break;
        }

        let activeKey = element.tabs[0]?.id ?? "";
        if (!IsNullOrWhiteSpace(this.state.tabsActive[key]))
            activeKey = this.state.tabsActive[key];
        let activeIndex = _.findIndex(element.tabs, t => t.id == activeKey);

        let reversed = false;
        let vertical = false;
        let flexDirection: 'column' | 'column-reverse' | 'row' | 'row-reverse' = 'column'

        switch (element.tabPosition) {
            case 'bottom':
                reversed = true;
                flexDirection = 'column-reverse'
                break;
            case 'left':
                vertical = true;
                flexDirection = 'row'
                break;
            case 'right':
                vertical = true;
                reversed = true;
                flexDirection = 'row-reverse'
                break;
        }

        let nav = (vertical == true) ?
            <Nav
                activeKey={activeKey}
                onSelect={(eventKey: string) => this.setState(s => {
                    s.tabsActive[key] = eventKey;
                    return s;
                })}
                appearance={appearance}
                reversed={reversed}
                vertical={vertical}
            >
                {_.map(element.tabs, e => {
                    let icon = undefined;
                    if (IsNullOrWhiteSpace(e.icon))
                        icon = <i className={e.icon} />;
                    return <Nav.Item key={e.id} eventKey={e.id} icon={icon} >{e.name}</Nav.Item>
                })}
            </Nav> :
            <ResponsiveNav
                activeKey={activeKey}
                onSelect={(eventKey: string) => this.setState(s => {
                    s.tabsActive[key] = eventKey;
                    return s;
                })}
                appearance={appearance}
                reversed={reversed}
                vertical={vertical}
            >
                {_.map(element.tabs, e => {
                    let icon = undefined;
                    if (IsNullOrWhiteSpace(e.icon))
                        icon = <i className={e.icon} />;
                    return <Nav.Item key={e.id} eventKey={e.id} icon={icon} >{e.name}</Nav.Item>
                })}
            </ResponsiveNav>;

        return <div key={key} style={{ display: 'flex', flexDirection: flexDirection }}>
            {nav}
            <div style={{ ...GetReactStyle(element.bodyStyle), flex: 1 }}>
                {activeIndex > -1 ? this.CreateGroup(element.tabs[activeIndex], groupLevel, groupId, false) : null}
            </div>
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    renderListOfItems(key: string, element: SettingsElementListOfItems<any>, groupLevel: number) {
        const { onElementChanged } = this.props;

        const fontColor = element.style?.fontColor;
        const backgroundColor = element.style?.backgroundColor;

        const leftIconSize = 20;
        const styles = {
            gridStyle: {
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'space-between',
                flexDirection: 'row',
                textAlign: 'center',
                alignItems: 'center'
            } as CSSProperties,

            mainBodyStyle: {
                display: 'flex',
                alignContent: 'center',
                alignItems: 'center'
            } as CSSProperties,

            mainBodyTitleStyle: {
                wordWrap: 'break-word'
            } as CSSProperties,

            mainBodySubtitleStyle: {
                fontSize: 12
            } as CSSProperties,

            iconStyle: {
                width: leftIconSize,
                minWidth: leftIconSize,
                height: leftIconSize,
                backgroundColor: backgroundColor,
                color: fontColor,
                fontSize: 16,
                display: 'flex',
                justifyContent: 'center',
                alignSelf: 'center',
                overflow: 'hidden',
                alignItems: 'center'
            } as CSSProperties
        };

        let selectedIds = [];
        let items: CustomListItem[] = [];
        let itemsLength = 0;

        if (!IsNullOrWhiteSpace(element.dataPath)) {
            items = _.cloneDeep(this.getValue(this.state.Data, element.dataPath));

            if (items?.length > 0) {
                //When we have a isNonSelectableAccessor we update itemsLength by taking into account only the selectable items.
                itemsLength = items?.filter(item => _.get(item, element.isNonSelectableAccessor) !== true)?.length;
            }
        }
        // Have default selected accessor
        if (IsNullOrWhiteSpace(element.selectedAccessor))
            element.selectedAccessor = "_isSelected";

        if (IsNullOrWhiteSpace(element.isNonSelectableAccessor))
            element.isNonSelectableAccessor = "_isNonSelectable";

        _.forEach(items, (el, index) => {
            //we want to be selected and enabled
            if (_.get(el, element.selectedAccessor) === true && _.get(el, element?.isNonSelectableAccessor) !== true) {
                let id = _.get(el, element.idAccessor);
                if (id != null)
                    selectedIds.push(id.toString());
                else
                    selectedIds.push(index.toString());
            }
        });

        const checkAll = selectedIds.length == itemsLength;
        const checkAllDisable = _.isEmpty(items);

        let checkAllElement = null;
        if (element.isSelectable) {
            checkAllElement = (
                <Checkbox
                    checked={checkAll}
                    disabled={checkAllDisable}
                    onChange={(value, checked) => {
                        this.setState((s: SettingsListState) => {
                            // set new data
                            let updatedData = this.getValue(s.Data, element.dataPath);
                            // keep previous data
                            let oldData = _.cloneDeep(updatedData);

                            if (isDefined(items) && items.length > 0)
                                items.forEach((el, index) => { if (_.get(el, element?.isNonSelectableAccessor) !== true) { _.set(updatedData[index], element.selectedAccessor, checked) } }); //TODO

                            this.setValueState(s, element.dataPath, updatedData);
                            if (isFunction(onElementChanged)) {
                                onElementChanged(element.dataPath, updatedData, oldData, s.Data);
                            }

                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave);
                    }}>
                    <span>{'Check all'}</span>
                </Checkbox>
            );
        }

        let searchElement = null;
        let filtered_data = items;
        if (element.isSearchable) {

            let searchValue = this.itemsListSearch[element.dataPath];
            if (searchValue == undefined) {
                // keep link from data path to search value
                this.itemsListSearch[element.dataPath] = null;
            }
            else if (!IsNullOrWhiteSpace(searchValue)) {
                // filter items
                filtered_data = _.filter(items, item => {
                    //let id: string = _.get(item, element.idAccessor);
                    let title: string = _.get(item, element.titleAccessor);
                    let subtitle: string = _.get(item, element.subtitleAccessor);

                    //id?.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
                    return title?.toLowerCase().indexOf(searchValue.toLowerCase()) > -1 ||
                        subtitle?.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
                });
            }

            searchElement = (
                <InputGroup
                    inside
                    style={{
                        width: '50%',
                        margin: '8px 0',
                        // put search bar at right if not selectable
                        left: element.isSelectable ? '0%' : '50%'
                    }}>
                    <Input
                        id={element.dataPath}
                        type='text'
                        style={{
                            // use element's theme
                            color: fontColor,
                            backgroundColor: backgroundColor
                        }}
                        value={searchValue}
                        onChange={(value, event) => {
                            this.itemsListSearch[element.dataPath] = value;
                            this.forceUpdate();
                        }} />
                    <InputGroup.Addon>
                        <i className='fas fa-search' style={{ color: fontColor }} />
                    </InputGroup.Addon>
                </InputGroup>
            );
        }

        let level = groupLevel + 1;
        return <div
            key={key}
            style={{
                paddingLeft: level > 0 ? level * 16 : undefined,
                width: '100%',
                ...GetReactStyle(element.style)
            }}>
            {element.isSelectable || element.isSearchable
                ? <FlexboxGrid>
                    <FlexboxGrid.Item colspan={24} style={styles.gridStyle}>
                        {checkAllElement}
                        {searchElement}
                    </FlexboxGrid.Item>
                </FlexboxGrid>
                : null}
            <List
                id="list-of-items-list"
                style={{ width: '100%', maxHeight: element.height }}
                autoScroll
                hover={element.isHoverable}
                size={element.listItemFormFactor}>
                {_.map(filtered_data, (item, itemIndex) => {
                    let leftIcon;
                    if (!IsNullOrWhiteSpace(element.leftIconDefault))
                        leftIcon = _.get(item, element.leftIconDefault);
                    if (!IsNullOrWhiteSpace(element.leftIconAccessor))
                        leftIcon = _.get(item, element.leftIconAccessor) ?? leftIcon;

                    let leftIconType = 'font';
                    if (isDefined(element.leftIconTypeAccessor)) {
                        leftIconType = _.get(item, element?.leftIconTypeAccessor) ?? 'font';
                    }

                    let title = null;
                    if (!IsNullOrWhiteSpace(element.titleAccessor))
                        title = _.get(item, element.titleAccessor);

                    let titleColor = null;
                    if (!IsNullOrWhiteSpace(element.titleColorAccessor))
                        titleColor = _.get(item, element.titleColorAccessor);

                    let subtitle = null;
                    if (!IsNullOrWhiteSpace(element.subtitleAccessor))
                        subtitle = _.get(item, element.subtitleAccessor);

                    let subtitleColor = null;
                    if (!IsNullOrWhiteSpace(element.subtitleColorAccessor))
                        subtitleColor = _.get(item, element.subtitleColorAccessor);

                    let tooltip = null;
                    if (!IsNullOrWhiteSpace(element.tooltipAccessor)) {
                        tooltip = _.get(item, element.tooltipAccessor);
                    }

                    let id = itemIndex.toString();
                    if (!IsNullOrWhiteSpace(element.idAccessor)) {
                        let eId = _.get(item, element.idAccessor);
                        if (!IsNullOrWhiteSpace(eId))
                            id = eId.toString();
                    }

                    //check if id is included in selectedIds Or in case that disabled button exist check initial list also
                    let selected = _.includes(selectedIds, id) || items?.findIndex(el => el.id === item.id && _.get(el, element.selectedAccessor)) > -1;
                    let disableItemCheckbox = _.get(item, element.isNonSelectableAccessor);
                    let onSelectedChange = () => {
                        this.setState((s: SettingsListState) => {
                            let data = this.getValue(s.Data, element.dataPath);
                            let oldData = _.cloneDeep(data);
                            let current_itemIndex = items.indexOf(item);

                            // Update selected accessor
                            _.set(data[current_itemIndex], element.selectedAccessor, !selected);

                            onElementChanged(element.dataPath, data, oldData, s.Data);
                            s.UnsavedData = !_.isEqual(s.Data, this.props.Data);
                            return s;
                        }, this.autoSave);
                    };

                    let showLeftIcon = !IsNullOrWhiteSpace(leftIcon);
                    let checkboxJSX = element.isSelectable ?
                        <Checkbox
                            disabled={disableItemCheckbox}
                            style={{ display: 'inline', flex: 'none' }}
                            value={id}
                            checked={selected}
                            onChange={onSelectedChange} /> : null;

                    // Set the body
                    let bodyJSX = <span
                        style={{
                            marginLeft: showLeftIcon ? 5 : 0,
                            flex: 'auto',
                            height: '100%',
                            ...styles.mainBodyStyle
                        }}
                        onMouseUp={onSelectedChange}
                        onMouseEnter={tooltip == null ? undefined : event => {
                            this._showTooltip(tooltip, event.pageX, event.pageY);
                        }}
                        onMouseLeave={tooltip == null ? undefined : this._hideTooltip}>
                        <div style={{
                            color: titleColor ?? fontColor ?? undefined,
                            ...styles.mainBodyTitleStyle
                        }}>
                            {title}
                        </div>
                        <div style={{
                            paddingTop: isDefined(title) ? 5 : 0,
                            color: subtitleColor ?? fontColor ?? "#97969B",
                            ...styles.mainBodySubtitleStyle
                        }}>
                            {subtitle}
                        </div>
                    </span>;

                    // Set the left icon
                    let leftIconJSX = null;
                    if (showLeftIcon) {
                        if (leftIconType == 'font') {
                            leftIconJSX = (
                                <i
                                    onMouseUp={onSelectedChange}
                                    style={{
                                        ...(styles.iconStyle ?? {}),
                                        flex: 'none'
                                    }}
                                    className={leftIcon} />
                            );
                        } else if (leftIconType == 'image') {
                            leftIconJSX = (
                                <img
                                    onMouseUp={onSelectedChange}
                                    style={{
                                        ...(styles.iconStyle ?? {}),
                                        flex: 'none'
                                    }}
                                    src={leftIcon} />
                            );
                        }
                    }

                    return <List.Item
                        key={itemIndex}
                        index={itemIndex}
                        style={{
                            width: '100%',
                            margin: undefined,
                            backgroundColor: 'transparent',
                            boxShadow: 'unset',
                            ...GetReactStyle(element.style)
                        }}>
                        <FlexboxGrid>
                            <FlexboxGrid.Item colspan={24} style={styles.gridStyle}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                    textAlign: 'left',
                                    width: '100%',
                                    cursor: element.isSelectable ? 'pointer' : undefined
                                }}>
                                    {checkboxJSX}
                                    {leftIconJSX}
                                    {bodyJSX}
                                    {_(element.rightButtons)
                                        .filter((rightButton, index) => {
                                            if (rightButton == null)
                                                return false;

                                            // Check if we have the accessor
                                            // If is null then we set the default
                                            if (!IsNullOrWhiteSpace(rightButton.isHiddenAccessor)) {
                                                let valid = _.get(item, rightButton.isHiddenAccessor);
                                                if (valid === true)
                                                    return false;
                                                if (valid === false)
                                                    return true;
                                            }

                                            if (_.has(rightButton, "isHiddenDefault"))
                                                return !rightButton.isHiddenDefault;

                                            return true;
                                        })
                                        .map((rightButton, index) => {
                                            return <Button
                                                key={rightButton.key}
                                                label={rightButton.label}
                                                labelAutoSize={false}
                                                borderRadius={rightButton.borderRadius}
                                                shadowLevel={rightButton.shadowLevel}
                                                tooltip={rightButton.tooltip}

                                                icon={rightButton.icon}
                                                iconFontSize={rightButton.iconSize}
                                                iconType={rightButton.type}
                                                iconFontColor={rightButton.fontColor ?? fontColor}

                                                style={{
                                                    width: rightButton.width ?? 25,
                                                    height: rightButton.height ?? 25,
                                                    minWidth: rightButton.width ?? 25,
                                                    backgroundColor: rightButton.backgroundColor,
                                                    color: rightButton.fontColor ?? fontColor,
                                                    fontSize: rightButton.fontSize,
                                                    margin: rightButton.margin,
                                                    textTransform: rightButton.uppercase ? 'uppercase' : 'initial',
                                                    paddingTop: 2,
                                                    paddingBottom: 2,
                                                    cursor: "pointer",
                                                    flex: "none"
                                                }}
                                                onClick={() => {
                                                    if (isFunction(this.props.onButtonPressed))
                                                        this.props.onButtonPressed({
                                                            element: element,
                                                            rightButtonClicked: {
                                                                key: rightButton.key,
                                                                buttonIndex: index
                                                            },
                                                            rowItem: item
                                                        }, this.state.Data);
                                                }}
                                            />
                                        }).value()}
                                </div>
                            </FlexboxGrid.Item>
                        </FlexboxGrid>
                    </List.Item>
                })}
            </List>
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    renderToolbar(toolbar: SettingsToolbarSettings) {
        if (toolbar == null)
            return null;

        // try get theme
        let boardTheme = BoardTheming.GetActiveTheme();
        let toolbarTheme = toolbar.theme ?? {
            height: 30,
            bgColor: "rgb(255, 158, 1)",
            iconColor: "black"
        };

        if (toolbarTheme.height == null)
            toolbarTheme.height = boardTheme.widgets.settingsList.toolbar.height;
        if (toolbarTheme.bgColor == null)
            toolbarTheme.bgColor = boardTheme.widgets.settingsList.toolbar.backgroundColor;
        if (toolbarTheme.iconColor == null)
            toolbarTheme.iconColor = boardTheme.widgets.settingsList.toolbar.iconColor;
        if (toolbarTheme.boxShadow == null)
            toolbarTheme.boxShadow = getZDepthShadow(boardTheme.widgets.settingsList.toolbar.shadowLevel);

        const icons: ReactNode[] = [];
        const rightIcons: ReactNode[] = [];

        if (toolbar.showCopy) {
            icons.push(
                <ToolbarIconButton
                    key="clipboard"
                    tooltip="Copy widget settings to clipboard"
                    className={"fas fa-clipboard"}
                    onClick={() => {
                        // todo: implement copy
                        CopyToClipboard(JSON.stringify(this.state.Data));
                    }}
                />
            );
        }

        if (isDefined(toolbar?.showCloseIcon)) {
            rightIcons.push(
                <ToolbarIconButton
                    key="close"
                    tooltip="Close"
                    className={"fas fa-times"}
                    style={{ color: toolbarTheme.iconColor }}
                    onClick={() => {
                        this.cancelData();
                    }}
                />
            );
        }
        return <RenderToolbar
            key="settings-list-toolbar"
            height={toolbarTheme.height}
            style={{
                backgroundColor: toolbarTheme.bgColor,
                color: toolbarTheme.iconColor,
                boxShadow: toolbarTheme.boxShadow
            }}
            LeftElements={icons}
            RightElements={rightIcons}
        />;
    }

    // --------------------------------------------------------------------------------------------
    renderHighlightsEditor(key: string, element: SettingsElementHighlights<any>, groupLevel: number) {
        const { Data } = this.state;
        // try parse `enableDecimal`
        let _isDecimal = false;
        if (isDefined(element.enableDecimal)) {
            if (typeof element.enableDecimal === "boolean")
                _isDecimal = element.enableDecimal === true;
            else
                _isDecimal = this.getValue(Data, String(element.enableDecimal));
        }

        // try parse `minValue`
        let _minValue = null;
        if (isDefined(element.minValue)) {
            if (typeof element.minValue === "number")
                _minValue = element.minValue;
            else
                _minValue = this.getValue(Data, String(element.minValue));
        }

        // try parse `maxValue`
        let _maxValue = null;
        if (isDefined(element.maxValue)) {
            if (typeof element.maxValue === "number")
                _maxValue = element.maxValue;
            else
                _maxValue = this.getValue(Data, String(element.maxValue));
        }

        let showTextColor = element.showTextColor === true;

        if (element.isMulti)
            return this.renderMultiHighlightsEditor(key, element, groupLevel, _minValue, _maxValue, showTextColor, _isDecimal);

        const itemsInfo: ItemsMgmtDialogInfo<any, any>[] = [
            {
                type: SettingsType.Number,
                label: "From",
                dataPath: 'from',
                isDecimal: _isDecimal,
                // uncomment after todo $1 implementation
                // minValue: _minValue,
                // maxValue: _maxValue
            },
            {
                type: SettingsType.Number,
                label: "To",
                dataPath: 'to',
                isDecimal: _isDecimal,
                // uncomment after todo $1 implementation
                // minValue: _minValue,
                // maxValue: _maxValue
            },
            {
                type: SettingsType.ColorPicker,
                label: "Color",
                dataPath: 'color'
            }
        ]

        if (showTextColor)
            itemsInfo.push({
                type: SettingsType.ColorPicker,
                label: "Text Color",
                dataPath: 'textColor'
            });

        let dialogElement: SettingsElementItemsMgmtDialog<any, any> = {
            label: element.label,
            type: SettingsType.ItemsMgmtDialog,
            dataPath: element.dataPath,
            dialogStaticSize: { w: 0.4, h: 0.4 },
            icon: element.icon,
            disable: element?.readonly ?? false,
            itemsInfo: itemsInfo,
            createNewItem: data => {
                return {
                    from: isDefined(_minValue) ? _minValue : null,
                    to: isDefined(_maxValue) ? _maxValue : null,
                    color: "rgba(0,0,0,1)",
                    textColor: showTextColor ? "rgba(0,0,0,1)" : undefined
                } as GaugeHelperHighlight;
            },
            onCheckData: data => {
                return validateHighlightsWithMsg(_minValue, _maxValue, data);
            }
        };
        return this.renderItemsMgmtDialog(key, dialogElement, groupLevel);
    }
    // --------------------------------------------------------------------------------------------
    renderMultiHighlightsEditor(key: string, element: SettingsElementHighlights<any>, groupLevel: number, minValue: number, maxValue: number, showTextColor: boolean, isDecimal: boolean) {
        let dialogElement: SettingsElementDialog<any> = {
            label: element.label,
            type: SettingsType.Dialog,
            dataPath: element.dataPath,
            icon: !isDefined(element.icon) ? <i className="fas fa-pen" style={{ fontSize: 16, color: element?.style?.fontColor }} /> : element.icon,
            dialogConf: {
                contentStaticSize: { w: 0.75, h: 0.85 },
                initPrivData: (data) => data,
                bodyFunc: (w, h, di) => {
                    return <MultiHighlightsEditor
                        width={w}
                        height={h}
                        layer2Highlights={di.privData}
                        showTextColor={showTextColor}
                        isVisible={true}
                        minValue={minValue}
                        maxValue={maxValue}
                        enableDecimal={isDecimal}
                        onSave={(newHighlights: GaugeHelperHighlightDictionary) => {
                            di.updateData(newHighlights);
                            di.closeDialogCB();
                        }}
                        onCancel={() => { di.closeDialogCB(); }}
                    />;
                }
            }
        };
        return this.renderDialog(key, dialogElement, groupLevel);
    }
    // --------------------------------------------------------------------------------------------
    render() {
        const onHoverHighlight = (this.props.onHoverHighlight) ? 'hover' : '';
        let listStyle: CSSProperties = {};
        if (this.props.style != null)
            listStyle = _.clone(this.props.style);
        if (this.state.UnsavedData &&
            this.props.HideUnsavedDataHighlight != true)
            listStyle.boxShadow = "rgba(0, 144, 162, 0.72) 0px 0px 20px 4px inset";

        if (this.props.numberOfGridColumn != null) {
            if (this.props.numberOfGridColumn == 1) {
                listStyle.gridTemplateColumns = "100%";
            } else {
                listStyle.gridTemplateColumns = "";
                for (let i = 0; i < this.props.numberOfGridColumn; i++)
                    listStyle.gridTemplateColumns += "1fr ";
            }
        }

        listStyle.padding = 0;
        if (this.props.gridGap != null)
            listStyle.gridGap = this.props.gridGap;

        let buttons = this.renderSettingsListButtons();
        return <>
            {this.renderToolbar(this.props.Toolbar)}

            <ListLite
                key="settings-list-list"
                className={"settings-list " + onHoverHighlight}
                style={listStyle}
            >
                {_(this.props.SettingsMap)
                    .map((m, i) => this.CreateGroup(m, 0, "/" + (m.groupKey ?? i)))
                    .flatMap()
                    .value()}
                {(buttons == null) ? null : <div
                    key="list-buttons"
                    style={{
                        gridColumn: "1 / " + ((this.props.numberOfGridColumn ?? 4) + 1),
                        width: "auto"
                    }}
                >
                    {buttons}
                </div>}
            </ListLite>

            <ExDialog
                key="settings-list-dialog"
                Enable={this.state.dialogConf != null}
                Conf={this.state.dialogConf}
            />

            <Infotip
                key="settings-list-infotip"
                open={this.state.tooltip.open}
                content={this.state.tooltip.content}
                xPos={this.state.tooltip.xPos}
                yPos={this.state.tooltip.yPos}
                maxWidth={350}
                style={{ zIndex: 12000 }}
            />
        </>
    }
    // --------------------------------------------------------------------------------------------
    renderSettingsListButtons() {
        if (this.props.AutoSave)
            return null;

        const { SettingsMap } = this.props;
        if (!_.isArray(SettingsMap) || SettingsMap.length == 0)
            return null;

        // get theme for cancel button
        if (isDefined(this.props.onCancelClick)) {
            return <>
                <Button
                    key="Cancel-Button"
                    label="Cancel"
                    height={40}
                    labelAutoSize={false}
                    borderRadius={10}
                    onClick={this.cancelData}
                    style={{
                        backgroundColor: '#E57373',
                        color: 'white',
                        margin: 10,
                        paddingTop: 2,
                        paddingBottom: 2,
                    }}
                    disabled={!this.state.UnsavedData}
                />

                <Button
                    key="Save-Button"
                    label="Save"
                    height={40}
                    labelAutoSize={false}
                    borderRadius={10}
                    onClick={this.saveData}
                    style={{
                        backgroundColor: '#64B5F6',
                        color: 'white',
                        margin: 10,
                        paddingTop: 2,
                        paddingBottom: 2,
                    }}
                    disabled={!this.state.UnsavedData}
                />
            </>;
        }

        return <Button
            key="Save-Button"
            label="Save"
            labelAutoSize={false}
            height={40}
            borderRadius={10}
            onClick={this.saveData}

            style={{
                backgroundColor: '#64B5F6',
                color: 'white',
                margin: 10,
                paddingTop: 2,
                paddingBottom: 2,
            }}
            disabled={!this.state.UnsavedData}
        />
    }
    // --------------------------------------------------------------------------------------------
    getSubGroupState(m: SettingsGroup<T>, groupId: string, state = this.state) {
        if (_.has(state.subGroupOpen, groupId))
            return state.subGroupOpen[groupId];
        else if (isDefined(m.startOpen))
            return m.startOpen;
        else
            return false;
    }
    // --------------------------------------------------------------------------------------------
    private CreateGroup(m: SettingsGroup<T>, groupLevel: number, groupId: string, enableGrouping: boolean = true) {
        let nestedElements = [];
        let elements: SettingsElementAll<any>[] = [];

        if (isDefined(m.elements))
            elements = m.elements;

        if (isDefined(m.elementsFunc)) {
            let elementFunc = m.elementsFunc(this.state.Data) as SettingsElementAll<T>[];
            if (isDefined(elementFunc))
                elements = elements.concat(elementFunc);
        }

        // Remove empty elements
        elements = _.compact(elements);

        let validFields = 0;
        _.forEach(elements, (element) => {
            if (element?.isHidden?.(this.state.Data) ?? false)
                return;
            validFields++;
        });

        if (validFields > 0) {
            if (!IsNullOrWhiteSpace(m.name) && enableGrouping) {
                groupLevel++;
                let groupID = groupLevel + "-" + groupId;
                nestedElements.push(<div
                    key={`element-group-${groupID}`}
                    style={{
                        gridColumn: "1 / " + ((this.props.numberOfGridColumn ?? 4) + 1),
                        paddingLeft: (groupLevel - 1) * 16,
                        margin: "0 3px",
                        //paddingBottom: this.getSubGroupState(m, groupID) ? 10 : 0,
                        width: "auto",
                    }}
                >
                    <div
                        className="settingslist-submenu"
                        style={{
                            width: "auto",
                            cursor: "pointer",
                            ...GetReactStyle(m.headerStyle)
                        }}
                        onClick={() => {
                            this.setState((s) => {
                                s.subGroupOpen[groupID] = !this.getSubGroupState(m, groupID, s)
                                return s;
                            });
                        }}
                        key={m.name}>
                        <div className="settingslist-submenu-icon-left">
                            {IsNullOrWhiteSpace(m.headerLeftIcon) ? null : <i className={getIconClassName(m.headerLeftIcon)} />}
                            {m.name}
                        </div>
                        <div className="settingslist-submenu-icon-right">
                            <i className={"fas " + ((this.getSubGroupState(m, groupID)) ? "fa-angle-down" : "fa-angle-left")} />
                        </div>
                    </div>
                </div>);
            }

            let subGroupId = groupLevel + "-" + groupId;
            if (groupLevel == 0 || this.getSubGroupState(m, subGroupId))
                _.forEach(elements, (element, elemIndex) => {
                    if (element?.isHidden?.(this.state.Data) ?? false)
                        return;
                    let elementRended = null;
                    let type = element.type;
                    if (type == SettingsType.Generic || type == SettingsType.GenericStr)
                        type = (element as SettingsElementGeneric<any>).typeFunc(this.state.Data);
                    let key = `el-${element.type}-${element.label}-${element.dataPath}-${subGroupId}-${elemIndex}`;

                    switch (type) {
                        case SettingsType.DropDownStr:
                        case SettingsType.DropDown:
                            elementRended = this.renderDropDownMenu(key, element as SettingsElementDropDown<any>, groupLevel);
                            break;
                        case SettingsType.BooleanStr:
                        case SettingsType.Boolean:
                            elementRended = this.renderBoolean(key, element as SettingsElementBoolean<any>, groupLevel);
                            break;
                        case SettingsType.ColorPickerStr:
                        case SettingsType.ColorPicker:
                            elementRended = this.renderColorPicker(key, m.name, element as SettingsElementColorPicker<any>, groupLevel);
                            break;
                        case SettingsType.NumberStr:
                        case SettingsType.Number:
                            elementRended = this.renderNumber(key, element as SettingsElementNumber<any>, groupLevel);
                            break;
                        case SettingsType.DatetimeStr:
                        case SettingsType.Datetime:
                            elementRended = this.renderDatetime(key, element as SettingsElementDatetime<any>, groupLevel);
                            break;
                        case SettingsType.DatetimeRangeStr:
                        case SettingsType.DatetimeRange:
                            elementRended = this.renderDatetimeRange(key, element as SettingsElementDatetimeRange<any>, groupLevel);
                            break;
                        case SettingsType.BarcodeStr:
                        case SettingsType.Barcode:
                            elementRended = this.renderBarcodeScanner(key, element as SettingsElementBarcodeScanner<any>, groupLevel);
                            break;
                        case SettingsType.TimeStr:
                        case SettingsType.Time:
                            elementRended = this.renderTime(key, element as SettingsElementTime<any>, groupLevel);
                            break;
                        case SettingsType.StringStr:
                        case SettingsType.String:
                            elementRended = this.renderString(key, element as SettingsElementString<any>, groupLevel);
                            break;
                        case SettingsType.DialogStr:
                        case SettingsType.Dialog:
                            elementRended = this.renderDialog(key, element as SettingsElementDialog<any>, groupLevel);
                            break;
                        case SettingsType.IconStr:
                        case SettingsType.Icon:
                            elementRended = this.renderIcon(key, element as SettingsElementIcon<any>, groupLevel);
                            break;
                        case SettingsType.ImageFileInputStr:
                        case SettingsType.ImageFileInput:
                            elementRended = this.renderImageFileInput(key, element as SettingsElementImageFileInput<any>, groupLevel);
                            break;
                        case SettingsType.SliderStr:
                        case SettingsType.Slider:
                            elementRended = this.renderSlider(key, element as SettingsElementSlider<any>, groupLevel);
                            break;
                        case SettingsType.ItemsMgmtDialogStr:
                        case SettingsType.ItemsMgmtDialog:
                            elementRended = this.renderItemsMgmtDialog(key, element as SettingsElementItemsMgmtDialog<any>, groupLevel);
                            break;
                        case SettingsType.ListItemStr:
                        case SettingsType.ListItem:
                            elementRended = this.renderListItem(key, element as SettingsElementListItem<any>, groupLevel);
                            break;
                        case SettingsType.GroupStr:
                        case SettingsType.Group: {
                            let elem = element as SettingsElementGroup<any>;
                            Array.prototype.push.apply(nestedElements, this.CreateGroup(elem, groupLevel, groupId + "-" + (elem.groupKey ?? elemIndex)));
                            break;
                        }
                        case SettingsType.MarkdownStringStr:
                        case SettingsType.MarkdownString:
                            elementRended = this.renderMarkdownString(key, element as SettingsElementMarkdownString<any>, groupLevel);
                            break;
                        case SettingsType.HighlightsStr:
                        case SettingsType.Highlights:
                            elementRended = this.renderHighlightsEditor(key, element as SettingsElementHighlights<any>, groupLevel);
                            break;
                        case SettingsType.ButtonStr:
                        case SettingsType.Button:
                            elementRended = this.renderButton(key, element as SettingsElementButton<any>, groupLevel);
                            break;
                        case SettingsType.ImageStr:
                        case SettingsType.Image:
                            elementRended = this.renderImage(key, element as SettingsElementImage<any>, groupLevel);
                            break;
                        case SettingsType.InfoTip:
                        case SettingsType.InfoTipStr:
                            elementRended = this.renderInfoTip(key, element as SettingsElementInfotip<any>, groupLevel);
                            break;
                        case SettingsType.TextEditor:
                        case SettingsType.TextEditorStringStr:
                            elementRended = this.renderTextEditorString(key, element as SettingsElementTextEditorString<any>, groupLevel);
                            break;
                        case SettingsType.ListOfItems:
                        case SettingsType.ListOfItemsStr:
                            elementRended = this.renderListOfItems(key, element as SettingsElementListOfItems<any>, groupLevel);
                            break;

                        case SettingsType.CodeEditor:
                        case SettingsType.CodeEditorStr:
                            elementRended = this.renderCodeEditor(key, element as SettingsElementCode<any>, groupLevel);
                            break;

                        case SettingsType.Attachments:
                        case SettingsType.AttachmentsStr:
                            elementRended = this.renderAttachments(key, element as SettingsElementAttachments<any>, groupLevel);
                            break;

                        case SettingsType.RadioButtons:
                        case SettingsType.RadioButtonsStr:
                            elementRended = this.renderRadioButtons(key, element as SettingsElementRadioButtons<any>, groupLevel);
                            break;
                        case SettingsType.ButtonGroup:
                        case SettingsType.ButtonGroupStr:
                            elementRended = this.renderButtonGroup(key, element as SettingsElementButtonGroup<any>, groupLevel);
                            break;
                        case SettingsType.TabsGroup:
                        case SettingsType.TabsGroupStr:
                            elementRended = this.renderTabsGroup(key, element as SettingsElementTabsGroup<any>, groupLevel, groupId);
                            break;
                        default:
                            break;
                    }

                    if (elementRended != null) {
                        // Custom style
                        let style = GetReactStyle(element.style) ?? {};

                        // grid column
                        style.gridColumn = "1 / " + ((this.props.numberOfGridColumn ?? 4) + 1);
                        if (element.gridStepsNumber != null) {
                            let steps = element.gridStepsNumber;
                            if (this.props.numberOfGridColumn != null &&
                                element.gridStepsNumber > this.props.numberOfGridColumn) {
                                steps = this.props.numberOfGridColumn;
                            }
                            style.gridColumn = "span " + steps;

                            if (steps != this.props.numberOfGridColumn && element.gridToRowEnd)
                                style.gridColumn += " / " + (this.props.numberOfGridColumn + 1);
                            else
                                style.gridColumn += " / auto";
                        }

                        nestedElements.push(<div
                            key={`el-${key} `}
                            style={style}>
                            {elementRended}
                        </div>)
                    }
                });
        }
        return nestedElements;
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
export interface SettingsListItemsDialogProps {
    Key: string;
    disabled?: boolean;
    element: SettingsElementItemsMgmtDialog<any>;
    groupLevel: number;
    isListItem: boolean;
    startOpen: boolean;
    Data: any;
    DataPath: string;
    onSaveData: (
        itemsMgmt: any[],
        itemsToolbarToggleState: { [key: string]: boolean },
        itemsToolbarDropdownState: { [key: string]: string | number },
    ) => void;
    showTooltip: (msg: string, x: number, y: number) => void;
    hideTooltip: () => void;
    onCancel?: () => void;
}
// ------------------------------------------------------------------------------------------------
export interface SettingsListItemsDialogState {
    OriginalItemsMgmt: any[];
    ItemsMgmt: any[];
    ItemsToolbarToggleState: { [key: string]: boolean };
    ItemsToolbarDropdownState: { [key: string]: string };
    DialogConf: DialogConf;
    Data: any;
    SubDialogConf: DialogConf;
    SubDialogConfstyle: CSSProperties;
    uploadingImg: boolean;
    tooltip: InfotipProps;
    filterValue: any;
}
// ------------------------------------------------------------------------------------------------
export class SettingsListItemsDialog extends Component<SettingsListItemsDialogProps, SettingsListItemsDialogState> {
    private headerCaching: ReactElement = null;
    private rowCaching: ReactElement[] = null;
    private invalidRow: number[] = [];
    private dialogPrivData: any = null;
    constructor(props: SettingsListItemsDialogProps) {
        super(props);

        let state = {
            Data: _.cloneDeep(this.props.Data),
            ItemsMgmt: null,
            OriginalItemsMgmt: null,
            ItemsToolbarToggleState: null,
            ItemsToolbarDropdownState: null,
            DialogConf: null,
            tooltip: {
                open: false,
                content: '',
                xPos: 0,
                yPos: 0
            },
            SubDialogConf: null,
            uploadingImg: false,
            SubDialogConfstyle: {},
            filterValue: null
        };

        if (isFunction(props.element.filteringList)) {
            let values = props.element.filteringList(props.Data);
            if (values.length > 0)
                state.filterValue = values[0].value;
        }

        if (this.props.startOpen)
            this._openDialog(state);

        this.state = state;
    }
    // --------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: SettingsListItemsDialogProps) {
        if (!_.isEqual(this.props.Data, prevProps.Data))
            this.setState({ Data: _.cloneDeep(this.props.Data) });
    }
    // --------------------------------------------------------------------------------------------
    getValue(data: any, path: string): any {
        if (IsNullOrEmpty(path))
            return data;
        return _.get(data, path);
    }
    // --------------------------------------------------------------------------------------------
    setValueState(state: SettingsListItemsDialogState, path: string, value: any) {
        if (IsNullOrEmpty(path))
            state.Data = value;
        else
            this.setValue(state.Data, path, value);
    }
    // --------------------------------------------------------------------------------------------
    setValue(data: any, path: string, value: any) {
        _.set(data, path, value);
    }
    // --------------------------------------------------------------------------------------------
    private CreateColorPicker(data: any, dataPath: string,
        isEnabled: boolean, idx: number, disableAlpha: boolean): ReactElement<any> {
        return <div>
            <Popover
                outsideNode={<div
                    style={{
                        width: 46,
                        height: 24,
                        padding: '5px',
                        background: '#fff',
                        borderRadius: '1px',
                        boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                        cursor: isEnabled ? 'pointer' : 'not-allowed',
                        opacity: isEnabled ? 1 : 0.5,
                        margin: "0 auto"
                    }}>
                    <div style={{
                        width: '36px',
                        height: '14px',
                        borderRadius: '2px',
                        background: this.getValue(data, dataPath)
                    }} />
                </div>}
                boxNode={<SketchPicker
                    disableAlpha={disableAlpha}
                    color={this.getValue(data, dataPath)}
                    onChange={(color) => {
                        this.setState((s: SettingsListItemsDialogState) => {
                            this.setValue(data, dataPath, "rgba(" + color.rgb.r + "," + color.rgb.g + "," + color.rgb.b + "," + color.rgb.a + ")");
                            this.invalidRow.push(idx);
                            return s;
                        });
                    }} />} />
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    private closeDialog = () => {
        this.setState((s: SettingsListItemsDialogState) => {
            s.SubDialogConf = null;
            return s;
        });
    }
    // --------------------------------------------------------------------------------------------
    private updateDialogPriv(data) {
        this.dialogPrivData = data;
        this.forceUpdate();
    }
    // --------------------------------------------------------------------------------------------
    private renderRow(item, idx) {
        let element = this.props.element;
        return <tr
            key={idx}
            className={(idx % 2 == 0) ? "settingslist-items-tr-even" : "settingslist-items-tr-odd"}
        >
            <td style={{ width: 50 }}>
                <i className='draggable-item fas fa-sort-alt center' style={{ fontSize: 16 }} />
            </td>
            {_.map(element.itemsInfo, (itemInfo, itemInfoIndex) => {

                let styleCell: CSSProperties = {};
                if (isFunction(itemInfo.styleCell))
                    styleCell = itemInfo.styleCell(this.state.Data, item, idx);

                return <td
                    style={styleCell}
                    key={"column-" + itemInfoIndex + "-" + idx}
                    colSpan={
                        // Define smaller area for small predefined items
                        (
                            itemInfo.type == SettingsType.ColorPicker || itemInfo.type == SettingsType.ColorPickerStr ||
                            itemInfo.type == SettingsType.Dialog || itemInfo.type == SettingsType.DialogStr ||
                            itemInfo.type == SettingsType.ItemsMgmtDialog || itemInfo.type == SettingsType.ItemsMgmtDialogStr ||
                            itemInfo.type == SettingsType.Boolean || itemInfo.type == SettingsType.BooleanStr ||
                            itemInfo.type == SettingsType.Icon || itemInfo.type == SettingsType.IconStr ||
                            itemInfo.type == SettingsType.MarkdownString || itemInfo.type == SettingsType.MarkdownStringStr || itemInfo.type == SettingsType.TextEditor || itemInfo.type == SettingsType.TextEditorStringStr

                        ) ? 1 : 2
                    }>
                    {(() => {
                        if (isFunction(itemInfo.isHiddenCell) &&
                            itemInfo.isHiddenCell(this.state.Data, item, idx))
                            return null;

                        let key = itemInfo.label + "-" + idx;
                        let isEnabled = true;
                        if (isFunction(itemInfo.isEnabled))
                            isEnabled = itemInfo.isEnabled(this.state.Data, item, idx);

                        // Check what type we are using in the end
                        let type = itemInfo.type;
                        if (isFunction(itemInfo.typeFuncCell))
                            type = itemInfo.typeFuncCell(this.state.Data, item, idx);

                        switch (type) {
                            case SettingsType.StringStr:
                            case SettingsType.String: {
                                const textInfo = itemInfo as SettingsElementString<any>;
                                let value = this.getValue(item, itemInfo.dataPath);
                                let errorText = null;
                                if (isDefined(itemInfo.isUnique) &&
                                    itemInfo.isUnique) {
                                    _.forEach(this.state.ItemsMgmt, (itemCheck, idxCheck) => {
                                        if (idx != idxCheck &&
                                            value == this.getValue(itemCheck, itemInfo.dataPath))
                                            errorText = "Duplicate " + itemInfo.label;
                                    })
                                }

                                if (!isDefined(value))
                                    value = "";

                                return <TextBox
                                    type="text"
                                    style={{ width: "100%", height: "100%" }}
                                    disabled={!isEnabled}
                                    key={key}
                                    readonly={textInfo.readonly}
                                    prefix={textInfo.prefix}
                                    prefixIcon={textInfo.prefixIcon}
                                    postfix={textInfo.postfix}
                                    postfixIcon={textInfo.postfixIcon}
                                    errorText={errorText}
                                    errorStyle={{ color: orange500 }}
                                    value={value}
                                    onChangeString={(newValue: string) =>
                                        this.setState((s: SettingsListItemsDialogState) => {
                                            this.setValue(s.ItemsMgmt[idx],
                                                itemInfo.dataPath,
                                                newValue);
                                            this.invalidRow.push(idx);
                                            return s;
                                        })
                                    }
                                />;
                            }

                            case SettingsType.DropDownStr:
                            case SettingsType.DropDown: {
                                const dropdownInfo = itemInfo as SettingsElementDropDown<any>;
                                let dropDownItems = dropdownInfo.items;
                                if (isFunction(itemInfo.itemsFunc)) {
                                    dropDownItems = itemInfo.itemsFunc(this.state.Data, item, idx);
                                }
                                const isMulti = dropdownInfo.isMulti === true;
                                const isCreatable = dropdownInfo.isCreatable === true;
                                const isGrouped = dropdownInfo.isGroupedItems === true;
                                return <Select
                                    options={_.map(dropDownItems, item => { return { label: item.label, value: item.value } })}
                                    isMulti={isMulti}
                                    maxNumOfSelections={dropdownInfo.maxNumOfSelections}
                                    isClearable={dropdownInfo.isClearable === true}
                                    isCreatable={isCreatable}
                                    valueStr={this.getValue(item, itemInfo.dataPath)}
                                    isDisabled={dropdownInfo.readonly || element.disable}
                                    dialogTitle={dropdownInfo.label}
                                    useDialog={dropdownInfo.useDialog}
                                    onChange={(data: any) => {
                                        this.setState((s: SettingsListItemsDialogState) => {
                                            // find new data
                                            let newData = handleSelectChange(data, dropDownItems, isMulti, isCreatable, isGrouped);
                                            // save data
                                            this.setValue(s.ItemsMgmt[idx], itemInfo.dataPath, newData);
                                            this.invalidRow.push(idx);
                                            return s;
                                        });
                                    }}
                                    containerStyle={{ width: "100%", padding: "0 4px" }}
                                />
                            }

                            case SettingsType.BooleanStr:
                            case SettingsType.Boolean:
                                return (this.getValue(item, itemInfo.dataPath)) ?
                                    <i key={key} className={"far fa-check-square center"}
                                        style={{ color: blue500, fontSize: 20 }} onClick={() => {
                                            this.setState((s: SettingsListItemsDialogState) => {
                                                this.setValue(s.ItemsMgmt[idx], itemInfo.dataPath, false);
                                                this.invalidRow.push(idx);
                                                return s;
                                            })
                                        }} /> :
                                    <i key={key} className={"far fa-square center"}
                                        style={{ fontSize: 20 }} onClick={() => {
                                            this.setState((s: SettingsListItemsDialogState) => {
                                                // Allow only one checked?
                                                if ((itemInfo as SettingsElementBoolean<any>).onlyOneChecked == true)
                                                    _.forEach(s.ItemsMgmt, (v, i) => {
                                                        if (i != idx) {
                                                            // Uncheck item's field.
                                                            this.setValue(v, itemInfo.dataPath, false);
                                                            this.invalidRow.push(i);
                                                        }
                                                    });

                                                this.setValue(s.ItemsMgmt[idx], itemInfo.dataPath, true);
                                                this.invalidRow.push(idx);
                                                return s;
                                            })
                                        }} />;

                            case SettingsType.ColorPickerStr:
                            case SettingsType.ColorPicker: {
                                return this.CreateColorPicker(
                                    item,
                                    itemInfo.dataPath,
                                    isEnabled,
                                    idx,
                                    (itemInfo as SettingsElementColorPicker<any>).disableAlpha == true);
                            }

                            case SettingsType.NumberStr:
                            case SettingsType.Number: {
                                let value = this.getValue(item, itemInfo.dataPath);
                                let errorText = null;
                                if (isDefined(itemInfo.isUnique) &&
                                    itemInfo.isUnique) {
                                    _.forEach(this.state.ItemsMgmt, (itemCheck, idxCheck) => {
                                        if (idx != idxCheck &&
                                            value == this.getValue(itemCheck, itemInfo.dataPath))
                                            errorText = "Duplicate " + itemInfo.label;
                                    })
                                }

                                if (!isDefined(value))
                                    value = "";

                                const _itemInfo = itemInfo as SettingsElementNumber<any>;
                                return <TextBox
                                    style={{ width: "100%", height: "100%" }}
                                    disabled={!isEnabled}
                                    key={key}
                                    value={value}
                                    errorText={errorText}
                                    errorStyle={{ color: orange500 }}
                                    type="number"
                                    step={_itemInfo.step}
                                    min={_itemInfo.minValue}
                                    max={_itemInfo.maxValue}
                                    prefix={_itemInfo.prefix}
                                    prefixIcon={_itemInfo.prefixIcon}
                                    postfix={_itemInfo.postfix}
                                    postfixIcon={_itemInfo.postfixIcon}
                                    // todo $1: validate min/max onblur!
                                    onChangeString={(newValue: string) => {
                                        try {
                                            let _value: number = _itemInfo.isDecimal ? parseFloat(newValue) : parseInt(newValue, 10);
                                            if (isDefined(_itemInfo.minValue) && _value < _itemInfo.minValue)
                                                _value = _itemInfo.minValue;
                                            if (isDefined(_itemInfo.maxValue) && _value > _itemInfo.maxValue)
                                                _value = _itemInfo.maxValue;
                                            this.setState((s: SettingsListItemsDialogState) => {
                                                this.setValue(
                                                    s.ItemsMgmt[idx],
                                                    itemInfo.dataPath,
                                                    _value
                                                );
                                                this.invalidRow.push(idx);
                                                return s;
                                            });
                                        } catch (ex) {
                                            DebugEx.TraceException(ex);
                                        }
                                    }}
                                />
                            }

                            case SettingsType.ItemsMgmtDialogStr:
                            case SettingsType.ItemsMgmtDialog:
                                return <SettingsListItemsDialog
                                    Key={key}
                                    disabled={!isEnabled}
                                    element={itemInfo as SettingsElementItemsMgmtDialog<any, any>}
                                    DataPath={this.props.DataPath + "[" + idx + "]." + itemInfo.dataPath}
                                    Data={this.state.Data}
                                    isListItem={false}
                                    startOpen={false}
                                    groupLevel={this.props.groupLevel + 1}
                                    onSaveData={(
                                        itemsMgmt: any[],
                                        itemsToolbarToggleState: { [key: string]: boolean },
                                        itemsToolbarDropdownState: { [key: string]: string }) => {
                                        this.setState((s: SettingsListItemsDialogState) => {
                                            this.setValue(
                                                s.ItemsMgmt[idx],
                                                itemInfo.dataPath,
                                                itemsMgmt
                                            );
                                            this.invalidRow.push(idx);
                                            // TODO: test if this create valid results

                                            _.forEach((itemInfo as SettingsElementItemsMgmtDialog<any, any>).toolbarToggles, t => {
                                                this.setValue(
                                                    s.ItemsMgmt[idx],
                                                    t.dataPath,
                                                    itemsToolbarToggleState[t.dataPath]
                                                );
                                            });
                                            _.forEach((itemInfo as SettingsElementItemsMgmtDialog<any, any>).toolbarDropdowns, d => {
                                                this.setValue(
                                                    s.ItemsMgmt[idx],
                                                    d.dataPath,
                                                    itemsToolbarDropdownState[d.dataPath]
                                                );
                                            });
                                            return s;
                                        })
                                    }}
                                    showTooltip={this.props.showTooltip}
                                    hideTooltip={this.props.hideTooltip}
                                />;

                            case SettingsType.DialogStr:
                            case SettingsType.Dialog:
                                return this.renderItemsDialog(key, item, itemInfo as SettingsElementDialog<any>, idx, isEnabled);

                            case SettingsType.ImageFileInputStr:
                            case SettingsType.ImageFileInput:
                                // TODO SettingsType.ImageFileInput
                                return null;

                            case SettingsType.SliderStr:
                            case SettingsType.Slider:
                                const sliderItemInfo = itemInfo as SettingsElementSlider<any>;
                                let sliderValue = this.getValue(item, itemInfo.dataPath);

                                return <ListLiteItem
                                    key={key}
                                    groupLevel={0}
                                    primaryText={sliderValue + " " + sliderItemInfo.valuePosfix}
                                    tooltip={element.tooltip}
                                    leftItem={element.leftIcon}
                                    rightItem={
                                        <Slider
                                            key={key + "-slider"}
                                            style={{ margin: '0 16px' }}
                                            disabled={!isEnabled}
                                            value={sliderValue}
                                            min={sliderItemInfo.minValue}
                                            max={sliderItemInfo.maxValue}
                                            step={sliderItemInfo.stepValue}
                                            onChange={value => this.setState((s: SettingsListItemsDialogState) => {
                                                this.setValue(s.ItemsMgmt[idx], itemInfo.dataPath, value);
                                                this.invalidRow.push(idx);
                                                return s;
                                            })}
                                        />
                                    }
                                />

                            case SettingsType.IconStr:
                            case SettingsType.Icon:
                                return this.renderIconSelector(item, itemInfo, idx, isEnabled);

                            case SettingsType.ExtendedIconStr:
                            case SettingsType.ExtendedIcon: {
                                // todo: use `IsEnabled` value
                                itemInfo = itemInfo as SettingsElementExtendedIcon<any>;
                                const iconTypePath = itemInfo.iconTypePath;

                                // Define the type of icon.
                                let iconType = this.getValue(item, iconTypePath);
                                if (isStringEmpty(iconType))
                                    iconType = eIconType.fontAwesome;

                                const updateItem = (value: string, iconType: eIconType = null) => {
                                    this.setState((s: SettingsListItemsDialogState) => {
                                        s.uploadingImg = false;

                                        // Update icon type (if provided).
                                        if (iconType != null)
                                            this.setValue(s.ItemsMgmt[idx], iconTypePath, iconType);

                                        // Update item's value.
                                        this.setValue(s.ItemsMgmt[idx], itemInfo.dataPath, value);
                                        this.invalidRow.push(idx);
                                        return s;
                                    });
                                };

                                // Useful constants.
                                const height = 40;
                                const value = this.getValue(item, itemInfo.dataPath);
                                const supportMore = itemInfo.supportUrl || isDefined(itemInfo.supportUpload);
                                // if upload is supported
                                if (isDefined(itemInfo.supportUpload)) {
                                    // initialize uploadSettings (if needed)
                                    itemInfo.supportUpload = _.defaults(itemInfo.supportUpload, {
                                        maxWidth: 24,
                                        maxHeight: 24,
                                        ext: "png",
                                        imgQuality: 0.8
                                    } as ExtendedIconUploadConf)
                                }
                                const { supportUpload } = itemInfo;
                                return <div style={{
                                    padding: '0 4px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    flexDirection: 'column'
                                }}>
                                    {iconType == eIconType.fontAwesome
                                        ? this.renderIconSelector(item, itemInfo, idx, isEnabled)
                                        : <>
                                            {iconType == eIconType.url
                                                ? <TextBox
                                                    type='text'
                                                    key={key}
                                                    value={value}
                                                    disabled={!isEnabled}
                                                    style={{
                                                        width: 'calc(100% - 96px)',
                                                        height: height,
                                                        verticalAlign: 'bottom',
                                                        backgroundColor: '#eeeeee'
                                                    }}
                                                    onChangeString={newValue => updateItem(newValue)}
                                                />
                                                : <div style={{
                                                    display: 'inline-block',
                                                    padding: 2,
                                                    backgroundColor: '#eeeeee'
                                                }}>
                                                    {!isStringEmpty(value)
                                                        ? <img
                                                            src={value}
                                                            width={40}
                                                            style={{
                                                                verticalAlign: 'initial',
                                                                margin: '0 2px'
                                                            }}
                                                        />
                                                        : null}
                                                    <label htmlFor={`file-${element.dataPath}`}>
                                                        <ToolbarIconButton
                                                            id="icons"
                                                            style={{ padding: 10, cursor: "pointer" }}
                                                            className="fas fa-arrow-alt-from-bottom" />
                                                    </label>
                                                    <input
                                                        type='file'
                                                        accept='image/*'
                                                        id={`file-${element.dataPath}`}
                                                        name={`file-${element.dataPath}`}
                                                        style={{ display: 'none' }}
                                                        onChange={ev => {
                                                            const input = ev.target as HTMLInputElement;
                                                            const files = input.files;

                                                            if (files && files.length > 0) {
                                                                const fr = new FileReader();
                                                                fr.onload = () => {
                                                                    isStringEmpty(fr.result)
                                                                        ? updateItem(null)
                                                                        : this.setState(
                                                                            { uploadingImg: true },
                                                                            () =>
                                                                                DomUtils
                                                                                    .getResizedImage(
                                                                                        fr.result as string,
                                                                                        supportUpload.maxWidth,
                                                                                        supportUpload.maxHeight,
                                                                                        supportUpload.ext,
                                                                                        supportUpload.imgQuality
                                                                                    )
                                                                                    .then(value => updateItem(value.src))
                                                                                    .catch(() => this.setState({ uploadingImg: false })))
                                                                };
                                                                fr.onerror = () => updateItem(null);
                                                                fr.readAsDataURL(files.item(0));
                                                            }
                                                        }} />
                                                    <ToolbarIconButton
                                                        className="fas fa-times"
                                                        style={{ padding: 10, cursor: "pointer" }}
                                                        onClick={() => updateItem(null)} />
                                                </div>}
                                        </>}
                                    {supportMore
                                        ? <Select
                                            isDisabled={!isEnabled}
                                            isMulti={false}
                                            isClearable={false}
                                            containerStyle={{ marginTop: 4 }}
                                            options={_.compact([{
                                                label: 'Font Awesome',
                                                value: eIconType.fontAwesome
                                            },
                                            itemInfo.supportUrl ? {
                                                label: 'URL',
                                                value: eIconType.url
                                            } : null,
                                            itemInfo.supportUpload ? {
                                                label: 'Upload',
                                                value: eIconType.hosted
                                            } : null
                                            ])}
                                            valueStr={iconType}
                                            onChange={value => updateItem(null, parseInt(value))}
                                        />
                                        : null}
                                </div>;
                            }

                            case SettingsType.MarkdownStringStr:
                            case SettingsType.MarkdownString: {
                                const info = itemInfo as SettingsElementDialog<any>;
                                let dialogItemInfo: SettingsElementDialog<any> = {
                                    label: info.label,
                                    type: SettingsType.Dialog,
                                    dataPath: info.dataPath,
                                    icon: info.icon,
                                    dialogConf: {
                                        contentStaticSize: { w: 0.8, h: 0.8 },
                                        bodyFunc: (w, h, di) => {
                                            return <MarkdownEditor
                                                width={w}
                                                height={h}
                                                Text={di.data}
                                                readOnly={info.readonly || info.disable}
                                                Title={info.label}
                                                onCloseRequest={() => di.closeDialogCB()}
                                                onSave={(newData) => di.updateData(newData)}
                                            />;
                                        }
                                    }
                                };
                                return this.renderItemsDialog(key, item, dialogItemInfo, idx, isEnabled);
                            }

                            case SettingsType.TextEditorStringStr:
                            case SettingsType.TextEditor: {
                                const info = itemInfo as SettingsElementDialog<any>;
                                let dialogItemInfo: SettingsElementDialog<any> = {
                                    label: info.label,
                                    type: SettingsType.Dialog,
                                    dataPath: info.dataPath,
                                    icon: info.icon,
                                    dialogConf: {
                                        contentStaticSize: { w: 0.8, h: 0.8 },
                                        bodyFunc: (w, h, di) => {
                                            return <TextEditorSettingsEditor
                                                width={w}
                                                height={h}
                                                Text={di.data}
                                                readOnly={info.readonly || info.disable}
                                                Title={info.label}
                                                onCloseRequest={() => di.closeDialogCB()}
                                                onSave={(newData) => di.updateData(newData)}
                                            />;
                                        }
                                    }
                                };
                                return this.renderItemsDialog(key, item, dialogItemInfo, idx, isEnabled);
                            }

                            case SettingsType.PaletteColorPickerStr:
                            case SettingsType.PaletteColorPicker:
                                return this.renderPaletteSelector(item, itemInfo, idx, isEnabled);

                            case SettingsType.DatetimeStr:
                            case SettingsType.Datetime: {
                                const element = itemInfo as SettingsElementDatetime<any>;
                                const format = migrateRsuiteDateFormatFromV4ToV5(element.format) ?? 'yyyy-MM-dd HH:mm:ss';

                                let value = this.getValue(item, itemInfo.dataPath);
                                let date = (!IsNullOrWhiteSpace(value)) ? moment(value).toDate() : null;
                                return <DatePicker
                                    key={"datepicker-edit"}
                                    block
                                    editable={false}
                                    isoWeek={element.isoWeek}
                                    format={format}
                                    value={date}
                                    onChange={(data) => {
                                        let value = (data == null) ? null : data.toISOString();
                                        this.setState((s: SettingsListItemsDialogState) => {
                                            this.setValue(
                                                s.ItemsMgmt[idx],
                                                itemInfo.dataPath,
                                                value);
                                            this.invalidRow.push(idx);
                                            return s;
                                        });
                                    }}
                                />;
                            }

                            case SettingsType.TimeStr:
                            case SettingsType.Time: {
                                // todo: use `isUnique` ?
                                let value = this.getValue(item, itemInfo.dataPath);
                                let date = isDefined(value) ? moment(value, 'HH:mm:ss').toDate() : null;
                                return <DatePicker
                                    block
                                    key='time-el'
                                    format='HH:mm'
                                    editable={false}
                                    value={isValidDate(date) ? date : null}
                                    onChange={time => this.setState((s: SettingsListItemsDialogState) => {
                                        this.setValue(
                                            s.ItemsMgmt[idx],
                                            itemInfo.dataPath,
                                            moment(time).format("HH:mm:ss"));
                                        this.invalidRow.push(idx);
                                        return s;
                                    })}
                                />;
                            }
                        }
                        return null;
                    })()}
                </td>;
            })}
            {element.readonly !== true ?
                <td style={{ width: 50 }}>
                    <i className={"fas fa-trash center"} style={{ fontSize: 16 }} onClick={() => {
                        this.setState((s: SettingsListItemsDialogState) => {
                            _.remove(s.OriginalItemsMgmt, x => x == s.ItemsMgmt[idx]);
                            this.filterItemsMgmt(s);
                            return s;
                        })
                    }} />
                </td>
                : null}
        </tr>
    }
    // --------------------------------------------------------------------------------------------
    renderIconSelector = (item, itemInfo, idx, enabled: boolean) => {
        const value = this.getValue(item, itemInfo.dataPath);
        return <IconButton
            key='settings-icon'
            iconStyle={{
                backgroundColor: grey50,
                borderRadius: "50%",
                width: 32,
                height: 32,
                color: blue500,
                margin: '0 auto'
            }}
            iconClassName={getIconClassName(value)}
            disabled={!enabled}
            onClick={() => {
                let value = _.cloneDeep(this.getValue(item, itemInfo.dataPath));
                this.updateDialogPriv(isDefined(value) ? value : '');
                this.setState({
                    SubDialogConf: {
                        title: "Select Icon",
                        contentStaticSize: { w: 0.5, h: 0.9 },
                        actionsFunc: () => [<IconButton
                            key="save-btn"
                            tooltip="Save"
                            iconClassName="fas fa-cloud-upload-alt"
                            onClick={() => {
                                this.setState((s: SettingsListItemsDialogState) => {
                                    this.setValue(
                                        s.ItemsMgmt[idx],
                                        itemInfo.dataPath,
                                        this.dialogPrivData
                                    );
                                    this.invalidRow.push(idx);
                                    return s;
                                })
                                this.closeDialog();
                            }}
                        />,
                        <IconButton
                            key="cancel-btn"
                            tooltip="Cancel"
                            iconClassName="fas fa-times"
                            iconStyle={{ color: red200 }}
                            onClick={this.closeDialog}
                        />],
                        bodyFunc: (w, h) => <Suspense fallback={<LazyLoadingAnimation />}>
                            <IconSelector
                                defaultIcon={{ className: value }}
                                width={w}
                                height={h}
                                onIconSelection={(iconInfo: IconInfo) => {
                                    this.updateDialogPriv(isDefined(iconInfo) ? iconInfo.className : '')
                                }}
                            />
                        </Suspense>
                    }
                });
            }} />;
    };
    // --------------------------------------------------------------------------------------------
    renderPaletteSelector = (item, itemInfo, idx, enabled: boolean) => {
        const value = this.getValue(item, itemInfo.dataPath);
        return <div
            style={{
                width: 46,
                height: 24,
                padding: '5px',
                background: '#fff',
                borderRadius: '1px',
                boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                cursor: enabled ? 'pointer' : 'not-allowed',
                opacity: enabled ? 1 : 0.5,
                margin: "0 auto"
            }}
            onClick={!enabled ? null : () => {
                let value = _.cloneDeep(this.getValue(item, itemInfo.dataPath));
                this.updateDialogPriv(isDefined(value) ? value : '');
                this.setState({
                    SubDialogConf: {
                        title: "Select Color",
                        contentStaticSize: { w: 0.5, h: 0.9 },
                        actionsFunc: () => [<IconButton
                            key="save-btn"
                            tooltip="Save"
                            iconClassName="fas fa-cloud-upload-alt"
                            onClick={() => {
                                this.setState((s: SettingsListItemsDialogState) => {
                                    this.setValue(
                                        s.ItemsMgmt[idx],
                                        itemInfo.dataPath,
                                        this.dialogPrivData
                                    );
                                    this.invalidRow.push(idx);
                                    return s;
                                })
                                this.closeDialog();
                            }}
                        />,
                        <IconButton
                            key="cancel-btn"
                            tooltip="Cancel"
                            iconClassName="fas fa-times"
                            iconStyle={{ color: red200 }}
                            onClick={this.closeDialog}
                        />],
                        bodyFunc: (w, h) => <ColorPaletteSelector
                            defaultColor={value}
                            width={w}
                            height={h}
                            onColorSelection={(color: string) => {
                                this.updateDialogPriv(color);
                            }}
                        />
                    }
                });
            }}>
            <div
                className={value}
                style={{
                    width: 36,
                    height: 14,
                    borderRadius: 2,
                }} />
        </div>;
    };
    // --------------------------------------------------------------------------------------------
    private renderItemsDialog(key: string, item: any, itemInfo: SettingsElementDialog<any>, idx, isEnabled: boolean) {
        return <div
            key={key}
            style={{
                width: 36, height: 36, lineHeight: "36px",
                cursor: !isEnabled ? "not-allowed" : "pointer",
                pointerEvents: !isEnabled ? "none" : "auto",
                textAlign: "center",
                margin: "0 auto"
            }}
            onClick={() => {
                let info = itemInfo as SettingsElementDialog<any>;
                let value = _.cloneDeep(this.getValue(item, info.dataPath));
                // Run  the init
                this.dialogPrivData = (isDefined(info.dialogConf.initPrivData)) ? info.dialogConf.initPrivData(value) : null;
                this.setState((s: SettingsListItemsDialogState) => {
                    let title;
                    if (isFunction(info.dialogConf.title))
                        title = (info.dialogConf.title as SettingsDialogTitleGetter)(item);
                    else
                        title = info.dialogConf.title;

                    s.SubDialogConf = {
                        title: title,
                        contentStaticSize: info.dialogConf.contentStaticSize,
                        actionsFunc: () => (!isDefined(info.dialogConf.actionsFunc)) ? null : info.dialogConf.actionsFunc({
                            rootData: this.state.Data,
                            itemIndex: idx,
                            data: value,
                            privData: this.dialogPrivData,
                            closeDialogCB: this.closeDialog,
                            updateData: (data) =>
                                this.setState((s: SettingsListItemsDialogState) => {
                                    this.setValue(
                                        s.ItemsMgmt[idx],
                                        itemInfo.dataPath,
                                        data
                                    );
                                    this.invalidRow.push(idx);
                                    return s;
                                }),
                            updatePrivData: (data) => this.updateDialogPriv(data)
                        }),
                        bodyFunc: (w, h) => info.dialogConf.bodyFunc(w, h, {
                            rootData: this.state.Data,
                            itemIndex: idx,
                            data: value,
                            privData: this.dialogPrivData,
                            closeDialogCB: this.closeDialog,
                            updateData: (data) =>
                                this.setState((s: SettingsListItemsDialogState) => {
                                    this.setValue(
                                        s.ItemsMgmt[idx],
                                        itemInfo.dataPath,
                                        data
                                    );
                                    this.invalidRow.push(idx);
                                    return s;
                                }),
                            updatePrivData: (data) => this.updateDialogPriv(data)
                        })

                    };
                    return s;
                });
            }} >
            {itemInfo.icon}
        </div>;
    }
    // --------------------------------------------------------------------------------------------
    private renderBodyItemsMgmtDialog(width: number, element: SettingsElementItemsMgmtDialog<any>) {
        // --------------------
        if (!isDefined(this.headerCaching))
            this.headerCaching = <tr className='settingslist-items-tr-header'>
                <td style={{ width: 50 }} />
                {_.map(element.itemsInfo, (item, itemIdex) =>
                    <td
                        key={"column-" + itemIdex}
                        colSpan={
                            // Define smaller area for small predefined items
                            (
                                item.type == SettingsType.ColorPicker ||
                                item.type == SettingsType.Dialog ||
                                item.type == SettingsType.ItemsMgmtDialog ||
                                item.type == SettingsType.Boolean ||
                                item.type == SettingsType.Icon ||
                                item.type == SettingsType.MarkdownString || item.type == SettingsType.TextEditor
                            ) ? 1 : 2
                        }>
                        <span
                            style={{ padding: 0 }}
                            onMouseEnter={e => {
                                this.props.showTooltip(
                                    IsNullOrWhiteSpace(item.tooltip) ? item.label : item.tooltip,
                                    e.pageX,
                                    e.pageY
                                );
                            }}
                            onMouseLeave={() => {
                                this.props.hideTooltip();
                            }}>
                            {item.label}
                        </span>
                    </td>
                )}
                {element.readonly !== true ? <td style={{ width: 50 }} /> : null}
            </tr>

        // --------------------

        if (!isDefined(this.rowCaching)) {
            this.rowCaching = _.map(this.state.ItemsMgmt, (item, idx) => this.renderRow(item, idx));
        } else {
            _.forEach(this.invalidRow, (idx) => {
                this.rowCaching[idx] = this.renderRow(this.state.ItemsMgmt[idx], idx);
            });
        }
        this.invalidRow = [];

        return <Suspense fallback={<LazyLoadingAnimation />}>
            <Sortable
                tableId='items-editor'
                tableHeader={this.headerCaching}
                items={this.rowCaching}
                updateItemsOrder={this.updateItemsOrder}
            />
        </Suspense>;
    }
    // --------------------------------------------------------------------------------------------
    updateItemsOrder = (oldIndex: number, newIndex: number) => this.setState((s: SettingsListItemsDialogState) => {
        // re-ordering original items
        let oldIndexOrig = _.findIndex(s.OriginalItemsMgmt, x => x == s.ItemsMgmt[oldIndex]);
        let newIndexOrig = _.findIndex(s.OriginalItemsMgmt, x => x == s.ItemsMgmt[newIndex]);
        s.OriginalItemsMgmt = arrayMoveImmutable(s.OriginalItemsMgmt, oldIndexOrig, newIndexOrig);
        this.filterItemsMgmt(s);
        return s;
    });
    // --------------------------------------------------------------------------------------------
    private filterItemsMgmt(s: SettingsListItemsDialogState) {
        if (isFunction(this.props.element.onFilteringRequest))
            s.ItemsMgmt = this.props.element.onFilteringRequest(s.Data, s.OriginalItemsMgmt, s.filterValue);
        else
            s.ItemsMgmt = s.OriginalItemsMgmt;
        this.rowCaching = null;
        this.invalidRow = [];
    }
    // --------------------------------------------------------------------------------------------
    private _openDialogState = () => this.setState((s: SettingsListItemsDialogState) => {
        this._openDialog(s);
        return s;
    });
    // --------------------------------------------------------------------------------------------
    renderIconButton() {
        const { Key, disabled, element } = this.props;
        return <div
            key={Key}
            style={{
                width: 36, height: 36, lineHeight: "36px",
                cursor: disabled ? "not-allowed" : "pointer",
                pointerEvents: disabled ? "none" : "auto",
                textAlign: "center",
                margin: "0 auto"
            }}
            onClick={disabled ? _.noop : this._openDialogState}>
            {isDefined(element.icon) ? element.icon : <i className="fas fa-pen" style={{ fontSize: 16, color: element?.style?.fontColor }} />}
        </div>
    }
    // --------------------------------------------------------------------------------------------
    private _openDialog(s: SettingsListItemsDialogState) {
        // Reset any caching
        this.rowCaching = null;
        this.headerCaching = null;
        this.invalidRow = [];

        const { Data, DataPath, element, onSaveData } = this.props;

        s.Data = _.cloneDeep(Data);
        s.OriginalItemsMgmt = this.getValue(s.Data, DataPath);
        // initialize to empty array if not defined
        if (!isDefined(s.OriginalItemsMgmt))
            s.OriginalItemsMgmt = [];
        this.filterItemsMgmt(s);

        // Init the toolbar items
        s.ItemsToolbarToggleState = {};
        s.ItemsToolbarDropdownState = {};
        _.forEach(element.toolbarToggles, t => {
            s.ItemsToolbarToggleState[t.dataPath] = this.getValue(s.Data, t.dataPath);
        });
        _.forEach(element.toolbarDropdowns, d => {
            s.ItemsToolbarDropdownState[d.dataPath] = this.getValue(s.Data, d.dataPath);
        });

        s.DialogConf = {
            title: element.label,
            contentStaticSize: (isDefined(element.dialogStaticSize)) ? element.dialogStaticSize : 0.8,
            actionsFunc: () => {
                let actions = _.map(element.toolbarToggles, (t, i) =>
                    <Toggle
                        key={t.label + "-" + i}
                        label={t.label}
                        style={{
                            width: 'auto',
                            color: 'white'
                        }}
                        defaultToggled={this.state.ItemsToolbarToggleState[t.dataPath]}
                        onToggle={(isInputChecked: boolean) =>
                            this.setState((s: SettingsListItemsDialogState) => {
                                s.ItemsToolbarToggleState[t.dataPath] = isInputChecked;
                                return s;
                            })} />);

                _.forEach(element.toolbarDropdowns, (d, i) => {
                    actions.push(<Select
                        key={`${d.label}-${i}`}
                        placeholder='View type'
                        controlStyle={{ minHeight: 'unset' }}
                        isMulti={false}
                        options={d.items}
                        valueStr={this.state.ItemsToolbarDropdownState[d.dataPath]}
                        onChange={value => {
                            this.setState((s: SettingsListItemsDialogState) => {
                                s.ItemsToolbarDropdownState[d.dataPath] = value;
                                return s;
                            });
                        }}
                    />);
                });
                // ------------
                let filterValues = null;
                if (isFunction(element.filteringList)) {
                    filterValues = element.filteringList(Data);
                    if (isDefined(filterValues)) {
                        actions.push(<Select
                            key='select'
                            valueStr={filterValues[0].value}
                            options={filterValues}
                            isClearable={false}
                            containerStyle={{ width: "200px" }}
                            isMulti={false}
                            onChange={(value) => {
                                this.setState((s: SettingsListItemsDialogState) => {
                                    s.filterValue = value;
                                    this.filterItemsMgmt(s);
                                    return s;
                                });
                            }}
                        />);
                    }
                }
                // ------------
                actions.push(<IconButton
                    key="download-btn"
                    tooltip='Download JSON'
                    iconClassName="fas fa-file-code"
                    onClick={() => {
                        let data = JSONSafeStringify(this.state.ItemsMgmt, undefined, "\t");
                        saveAs(new Blob([data], { type: "text/plain;charset=utf-8" }), "Items.json");
                    }} />);
                // ------------
                if (element.readonly !== true && isDefined(element.createNewItem))
                    actions.push(<IconButton
                        key="add-btn"
                        tooltip="Add Element"
                        iconClassName="fas fa-plus"
                        iconStyle={{ color: "white" }}
                        onClick={() => {
                            this.setState((s: SettingsListItemsDialogState) => {
                                let data = null;
                                if (isFunction(element.createNewItem))
                                    try { data = element.createNewItem(this.state.Data, this.state.filterValue); }
                                    catch {
                                        return s;
                                    }
                                else
                                    data = _.cloneDeep(element.createNewItem);

                                s.OriginalItemsMgmt.push(data);
                                this.filterItemsMgmt(s);
                                return s;
                            });
                        }} />);
                // ------------
                actions.push(<IconButton
                    key="save-btn"
                    tooltip="Save"
                    iconClassName="fas fa-cloud-upload-alt"
                    onClick={() => {
                        // validate before save
                        const { OriginalItemsMgmt, ItemsToolbarToggleState, ItemsToolbarDropdownState } = this.state;
                        if (isDefined(element.onCheckData)) {
                            let v = element.onCheckData(OriginalItemsMgmt);
                            if (!v.valid) {
                                this.setState({
                                    SubDialogConf: {
                                        title: "Error",
                                        actions: [<IconButton
                                            key="close-btn"
                                            tooltip="Close"
                                            iconClassName="fas fa-times"
                                            iconStyle={{ color: red200 }}
                                            onClick={() => this.setState({ SubDialogConf: null })}
                                        />],
                                        body: <div style={{ padding: 24 }}>{v.errorMsg}</div>
                                    },
                                    SubDialogConfstyle: { marginTop: 50 }
                                });
                                return;
                            }
                        }
                        onSaveData(OriginalItemsMgmt, ItemsToolbarToggleState, ItemsToolbarDropdownState);
                        this.setState({ DialogConf: null });
                    }}
                />);
                // ------------
                actions.push(<IconButton
                    key="cancel-btn"
                    tooltip="Cancel"
                    iconClassName="fas fa-times"
                    iconStyle={{ color: red200 }}
                    onClick={() => {
                        RunSafe(this.props.onCancel);
                        this.setState({ DialogConf: null });
                    }} />);
                // ------------
                return actions;
            },
            bodyFunc: (w, h) => this.renderBodyItemsMgmtDialog(w, element)
        } as DialogConf;

    }
    // --------------------------------------------------------------------------------------------
    render() {
        const { DialogConf, SubDialogConf, SubDialogConfstyle } = this.state;
        if (this.props.isListItem) {
            const { element } = this.props;
            return <>
                <ListLiteItem
                    tooltip={element.tooltip}
                    primaryText={element.label}
                    key={element.type + "-" + element.label}
                    groupLevel={this.props.groupLevel}
                    leftItem={element.leftIcon}
                    rightItem={this.renderIconButton()}
                />
                {DialogConf == null ? null : <ExDialog
                    key={this.props.Key + "-dialog"}
                    Enable={DialogConf != null}
                    Conf={DialogConf}
                />}
                {SubDialogConf == null ? null : <ExDialog
                    key={this.props.Key + "-dialog-sub"}
                    Enable={SubDialogConf != null}
                    Conf={SubDialogConf}
                    style={SubDialogConfstyle}
                />}
            </>;
        }

        else if (DialogConf == null && SubDialogConf == null)
            return this.renderIconButton();
        else
            return [
                this.renderIconButton(),
                <ExDialog
                    key={this.props.Key + "-dialog"}
                    Enable={DialogConf != null}
                    Conf={DialogConf}
                />,
                <ExDialog
                    key={this.props.Key + "-dialog-sub"}
                    Enable={SubDialogConf != null}
                    Conf={SubDialogConf}
                />
            ];
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
function handleSelectChange(data: any, items: DropDownItem[], isMulti: boolean, isCreatable: boolean, isGrouped: boolean): any {
    if (isGrouped) {
        items = _.flatMap(items, i => i.options);
    }

    if (isMulti != true) {
        let val = _.find(items, i => isDefined(i.value) && i.value.toString() == data);
        if (isDefined(val)) {
            return val.value;
        } else if (isCreatable) {
            return data;
        }
        return null;
    }

    let results = [];
    if (isString(data) && !isStringEmpty(data)) {
        let values = data.split(",");
        _.forEach(values, v => {
            let val = _.find(items, i => isDefined(i.value) && i.value.toString() == v);
            if (isDefined(val)) {
                results.push(val.value);
            } else if (isCreatable) {
                results.push(v);
            }
        });
    }
    return results.join(",");
}
// ------------------------------------------------------------------------------------------------
function isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
}
// ------------------------------------------------------------------------------------------------
export default SettingsList;